// Modo "Crear desde cero" — sheet del agente V2 inline.
//
// Tiene DOS CAMINOS, gobernados por el toggle "Ayuda de IA" (ADR-2026-08-19):
//
//   • DIRECTO (default, `aiHelp === false`): el prompt NO pasa por el LLM.
//     `handleGenerate` traduce el estado de la UI a una tool card y la empuja
//     a mano con `chat.pushToolCard` — mismo mecanismo scripted que el botón
//     "Sorpréndeme" de InterfazV2. Como NO se llama `pushUserMessage`, no hay
//     burbujas de chat: el panel queda como una tira de piezas generadas.
//     Sin aura. El toggle Imagen/Video decide qué tool se dispara.
//   • AYUDA DE IA (`aiHelp === true`): el agente conversacional de siempre,
//     con aura, y el toggle viajando como pista invisible vía `contextNote`.
//
// Y DOS ESTADOS VISUALES, ortogonales a lo anterior:
//
//   • Empty (sin mensajes): título "<verbo> sin límites" —el verbo se tipea y
//     rota solo, ver `asistente/VerboTipeado`— + cards de acción +
//     toggle + input. Es la "home" del modo.
//   • Active (después de la primera pieza o mensaje): el input se ancla al
//     pie del sheet y el área superior se vuelve el scroll de la sesión
//     (mensajes/tool cards). Los componentes de mensaje se importan de
//     ChatPanelV2 para no duplicar lógica.
//
// El cluster de blurs azules ("el aura") viaja por detrás de TODO el
// contenido en ambos estados, pero SOLO existe con Ayuda de IA prendida.
//
// Y DOS VARIANTES, por el prop `variante`
// (ADR-2026-09-08-asistente-v2-es-una-variante-del-mismo-modo):
//
//   • `v1` — `/modo/asistente`, "Crear desde cero". Todo lo de arriba tal cual.
//   • `v2` — `/modo/asistente-v2`, "Asistente v2". Sin el switch Imagen/Video y sin el
//     desplegable de Ayuda de IA: la ayuda va prendida por detrás (o sea que de los dos
//     caminos de arriba sólo existe el del agente, con aura) y si querés un video se lo
//     pedís por texto. El `outputKindHint` no viaja. Como su único camino es la opción
//     Pro, el MODO pide Pro/Ultra — `getModePlanLock('asistente-v2')`, y no los
//     candados por opción de v1.

import { formatoEstandarDe } from '../../../utils/formatoPersonalizado';
import React, { useState, useEffect, useRef, useCallback, useMemo } from 'react';
import ReactDOM from 'react-dom';
import { useNavigate } from 'react-router-dom';
import LoadingAnimation from '../../../components/LoadingAnimation';
import {
  ChevronLeft,
  ChevronRight,
  Plus,
  Upload,
  Package,
  User,
  Image as ImageIcon,
  Smartphone,
  Layers,
  MessageSquare,
  SquarePen,
  History,
  Trash2,
  MoreHorizontal,
  Coins,
  Brain,
  X,
  Square,
  Sparkles,
  Mic,
  PersonStanding,
  Paperclip,
  Video,
  type LucideIcon,
} from 'lucide-react';
import DictadoVoz, { dictadoDisponible } from './asistente/DictadoVoz';
import {
  type UseV2ChatReturn,
  isInlinePendingCard,
  clavesDeStorage,
} from '../useV2Chat';
import { PosePickerSheet } from '../PosePicker';
import { fetchAllPoses, type PoseV2 } from '../posesApi';
import {
  listChats,
  deleteChats,
  type AgentChatSummary,
} from '../agentChatApi';
import { tipoDeActividad } from '../actividadDelAgente';
import { filasDeLaConversacion } from '../accionesDelTurno';
import {
  LineasDeAcciones,
  MessageRow,
  ThinkingIndicator,
  ToolCardRenderer,
  ProductFullMenu,
} from '../ChatPanelV2';
import {
  ModelPickerSheet,
  modelSelectionLabel,
  type ModelSelection,
} from '../ModelPicker';
import { useUserProducts } from '../productsContext';
import { useUserModels } from '../modelsContext';
import { useAgentEnv } from '../agentEnvContext';
import {
  uploadReferenceImage,
  signReferenceImage,
  type ChatAttachment,
} from '../agentAttachments';
import GeneratedImagesPicker, {
  type GeneratedPick,
} from '../GeneratedImagesPicker';
import { type Intelligence, intelligenceToModel } from '../agentModels';
import { OutputKindToggle } from './asistente/OutputKindToggle';
import { AccionCardsRow } from './asistente/AccionCardsRow';
import { PREGUNTAS_DEMO } from '../components/PreguntasCard';
import { AyudaIAMenu } from './asistente/AyudaIAMenu';
import { ConfiguracionMenu } from './asistente/ConfiguracionMenu';
import { ReferenciasSheet } from './asistente/ReferenciasSheet';
import { ListaHistorialChats, ListaMemorias } from './asistente/PanelesChat';
import {
  FormatoInline,
  CantidadInline,
} from './asistente/ControlesGeneracion';
import { VerboTipeado, VERBOS } from './asistente/VerboTipeado';
import {
  buildDirectToolCall,
  clampDuracion,
  creditsForDirectCall,
  type OutputKind,
  type VideoQuality,
} from './asistente/directMode';
import type { VideoResolution } from '../../../utils/credits';
import { DEFAULT_FORMAT_ID } from '../mockData';
import {
  fetchMemories,
  deleteMemory,
  type AgentMemory,
} from '../agentMemoryApi';
import {
  EncabezadoPantalla,
  MARCO_PANTALLA,
  BORDE_PANTALLA,
} from '../../../components/PantallaChrome';
import { nombreCortoModo } from '../modesCatalog';
// Gating de las dos opciones pagas del modo —
// ADR-2026-08-20-crear-desde-cero-se-abre-y-el-candado-baja-a-las-opciones.
import {
  getOpcionAsistenteLock,
  puedeUsarAyudaIA,
  puedeGenerarVideoEnAsistente,
  type ModePlanLock,
} from '../../../lib/planCapabilities';
import { getUserPlan } from '../../../lib/planUtils';
import { FeatureLockedModal } from '../../../components/FeatureLockedModal';
import { miniaturaDeAdjunto } from '../etiquetaDeAdjunto';
import { ModalConfirmar } from '../../../components/ModalConfirmar';
import { CumbreMascot } from '../../../components/CumbreMascot';
import { palabraDeModeloActual } from '../../../lib/nombreDeModelo';

/** Los dos botones de esquina del encabezado de v2 — "Crear nuevo chat"
 *  (izquierda) e "Historial de chats" (derecha). Comparten una sola constante
 *  porque son un PAR: el pedido fue explícito de que se vean iguales, y dos
 *  className copiados se desincronizan al primer retoque.
 *
 *  Ícono + rótulo visible. Es la geometría del pill "Nuevo chat" que v1 tiene a
 *  la derecha, pero relleno gris claro y SIN sombra ni borde: son chrome, y
 *  blanco-sobre-blanco con sombra los levantaba del fondo como si fueran la
 *  acción principal de la pantalla. El gris es el mismo `neutral-100` de los
 *  botones redondos del composer, así que los controles secundarios de la
 *  pantalla comparten relleno.
 *
 *  El rótulo es también el nombre accesible: por eso no llevan `aria-label`, que
 *  sólo serviría para decir otra cosa que lo que se lee. */
const BOTON_ESQUINA =
  'inline-flex items-center gap-1.5 py-2 px-4 rounded-full bg-neutral-100 hover:bg-neutral-200 text-[13.5px] font-semibold text-[rgb(var(--brand-black-rgb)_/_0.9)] transition-colors';

/**
 * Sugerencias del empty state — los pills entre el título y el input.
 *
 * `label` es lo que se lee (corto, cabe en un pill); `prompt` es lo que
 * realmente se manda. Son distintos a propósito: el pill tiene que entrar en una
 * fila, pero al agente le sirve una frase entera. Un click manda el `prompt` y
 * arranca el turno — no pre-llena el textarea, que era la mitad de un gesto.
 *
 * Sólo se muestran CON Ayuda de IA. En modo directo (sólo v1) un click empujaría
 * la tool card y gastaría créditos sin confirmación de nadie; con el agente, en
 * cambio, el turno se puede redirigir con el mensaje siguiente.
 */
const SUGERENCIAS: { key: string; label: string; prompt: string; Icon: LucideIcon }[] = [
  {
    key: 'producto',
    label: 'Fotos de mi producto',
    prompt: 'Quiero fotos profesionales de mi producto para la tienda',
    Icon: ImageIcon,
  },
  {
    key: 'modelo',
    get label() {
      return `Mi producto en un ${palabraDeModeloActual().singular}`;
    },
    get prompt() {
      return `Quiero fotos de mi producto puesto sobre un ${palabraDeModeloActual().singular}`;
    },
    Icon: PersonStanding,
  },
];

export interface AsistenteMarketingSheetProps {
  animated: boolean;
  sidebarWidth: number;
  onClose: () => void;
  /** Instancia del chat del agente V2 — viene del padre (InterfazV2) para
   *  que se mantenga vivo aunque el sheet se cierre y reabra (el padre la
   *  resetea cuando corresponde). El sheet la consume vía MessageRow /
   *  ToolCardRenderer / sendMessage. */
  chat: UseV2ChatReturn;
  /**
   * Qué variante del modo se monta — ADR-2026-09-08-asistente-v2-es-una-variante-del-mismo-modo.
   *
   *   • `'v1'` (default, ruta `/modo/asistente`, "Crear desde cero"): la pantalla
   *     completa, con el switch Imagen/Video y el desplegable de Ayuda de IA. Los DOS
   *     caminos del encabezado de este archivo siguen vivos.
   *   • `'v2'` (ruta `/modo/asistente-v2`, "Asistente v2"): SIN esos dos controles. La
   *     Ayuda de IA va prendida por detrás —o sea que sólo existe el camino con
   *     agente— y el tipo de salida se pide por texto ("hacéme un video de…"), así que
   *     tampoco viaja el `outputKindHint`.
   *
   * Es un prop y no un archivo aparte a propósito: las dos variantes se van a comparar
   * entre sí, y con una copia de 2300 líneas cada arreglo que entre en una y no en la
   * otra convierte la comparación en ruido. Lo que cambia son tres condicionales de
   * render y el clamp de `aiHelp`.
   */
  variante?: 'v1' | 'v2';
}

export const AsistenteMarketingSheet: React.FC<AsistenteMarketingSheetProps> = ({
  animated,
  sidebarWidth,
  onClose,
  chat,
  variante = 'v1',
}) => {
  const esV2 = variante === 'v2';
  /* ¿Hay conversación empezada? Va ACÁ ARRIBA, y no junto a `hasText` /
     `agenteConversando` como sus hermanos, por una razón concreta: es una `const`, o
     sea que tiene zona muerta temporal, y uno de los efectos de scroll de más abajo la
     lista en sus dependencias. Las deps se evalúan EN EL RENDER, así que declarada
     debajo de ese efecto tiraba `Cannot access 'hasMessages' before initialization` y
     el ErrorBoundary se comía la pantalla entera del modo
     (ADR-2026-09-08-hasmessages-se-declara-antes-que-los-efectos).

     Declarada arriba del todo no hay orden que la rompa: sólo depende del prop `chat`.
     Si la movés abajo, volvé a mirar qué efecto la usa en sus deps. */
  const hasMessages = chat.messages.length > 0;
  // El composer colapsado de v2 cambia el placeholder por breakpoint (ver más abajo):
  // la copia larga no entra en una fila que ya lleva tres controles.
  const [prompt, setPrompt] = useState<string>('');
  // Dictado por voz: cuando está activo, el contenido del composer (textarea
  // + fila de controles) se reemplaza por <DictadoVoz>. Solo se ofrece con
  // el prompt vacío — el mic ocupa el lugar de la flecha muerta.
  const [dictando, setDictando] = useState(false);
  // Foco del textarea. En mobile, cuando el input toma foco (aparece el
  // teclado) en el empty-state, ocultamos sutilmente "Crea sin límites" y
  // mostramos las sugerencias sin su pill.
  const [inputFocused, setInputFocused] = useState<boolean>(false);
  // Mobile: con el teclado abierto fijamos el sheet al área visible exacta que
  // reporta visualViewport (top = offsetTop, height = vv.height) en vez de
  // dejarlo `inset-0`. Así el input queda justo arriba del teclado y se compensa
  // el scroll que hace iOS del visual viewport (sin esto el contenido se corría
  // hacia arriba y el input quedaba cortado). Vacío (= inset-0) con teclado
  // cerrado / desktop. Lo recalcula el efecto de visualViewport.
  const [vvStyle, setVvStyle] = useState<React.CSSProperties>({});
  // Nivel de "inteligencia" (modelo). Persistido en localStorage; default
  // 'baja' (Gemini Flash — chatear no consume créditos, ADR-608). 'media'
  // usa GPT-6 Sol y 'alta' Opus. Se sincroniza con chat.setModel.
  const [intelligence, setIntelligence] = useState<Intelligence>(() => {
    try {
      const v = localStorage.getItem('atlas-v2-intelligence');
      if (v === 'baja' || v === 'media' || v === 'alta') return v;
    } catch {
      /* noop */
    }
    return 'baja';
  });
  const setModel = chat.setModel; // estable (useCallback) — evita re-runs
  useEffect(() => {
    setModel(intelligenceToModel(intelligence));
    try {
      localStorage.setItem('atlas-v2-intelligence', intelligence);
    } catch {
      /* noop */
    }
  }, [intelligence, setModel]);

  /* ── Plan, para gatear las dos opciones caras ───────────────────────────────
     ADR-2026-08-20-crear-desde-cero-se-abre-y-el-candado-baja-a-las-opciones.

     El modo dejó de pedir Pro/Ultra para ENTRAR, así que ahora esta pantalla la abre
     cualquiera —incluida la prueba gratis— y es acá adentro donde hay que decidir qué
     puede prender. Este archivo no leía el plan porque hasta ahora no le hacía falta.

     Mismo arranque canónico que `/modos` y el drawer: el cache que ya escribe
     `useCuentaAtlas` para tener el candado desde el primer paint, y el valor real
     después. Con el plan desconocido NO se bloquea — mismo criterio que
     `getModePlanLock`, y el corte del submit queda de backstop. */
  const [plan, setPlan] = useState<string | null>(() => {
    try {
      return localStorage.getItem('atlas-user-plan');
    } catch {
      return null;
    }
  });
  useEffect(() => {
    let cancelado = false;
    getUserPlan().then((p) => {
      if (!cancelado && p) setPlan(p);
    });
    return () => {
      cancelado = true;
    };
  }, []);
  /** Lock de opción a mostrar (ayuda de IA o video), o `null`. */
  const [lockOpcion, setLockOpcion] = useState<ModePlanLock | null>(null);

  // ── Modo directo vs "Ayuda de IA" (ADR-2026-08-19) ─────────────────
  // Apagada = modo DIRECTO (el default): el prompt va al motor de
  // generación sin pasar por el agente, y el aura no se monta.
  //
  // El CLAMP va en el initializer y no sólo en el handler —
  // ADR-2026-08-20-crear-desde-cero-se-abre-y-el-candado-baja-a-las-opciones—: esta
  // clave es persistente, así que alguien que la prendió siendo Pro y después bajó de
  // plan (o que comparte el navegador con una cuenta Pro) rehidrataba la opción
  // prohibida y el candado no se enteraba nunca, porque el handler sólo corre cuando
  // se TOCA el control.
  //
  // Ojo con el orden: `plan` arranca del cache de localStorage, así que en el primer
  // render ya vale lo que corresponde. Si el cache está vacío, `puedeUsarAyudaIA('')`
  // da `false` y el clamp apaga la opción; cuando llegue el plan real, si es Pro el
  // efecto de más abajo NO la vuelve a prender —queda apagada, que es el lado seguro—
  // y el usuario la prende con un click.
  const [aiHelpState, setAiHelp] = useState<boolean>(() => {
    try {
      if (localStorage.getItem('atlas-v2-ai-help') !== '1') return false;
      return puedeUsarAyudaIA(localStorage.getItem('atlas-user-plan'));
    } catch {
      return false;
    }
  });
  /* En v2 la Ayuda de IA está SIEMPRE prendida: no hay desplegable con el que apagarla,
     así que el estado de arriba —y el valor que quedó en localStorage de una sesión en
     v1— no la gobiernan. Se deriva en vez de forzarse con un `setAiHelp(true)` en un
     efecto para que no haya un primer paint en modo directo (título negro, sin aura)
     antes de que el efecto corra, y para que v2 no le pise el toggle guardado a v1.

     El acceso a v2 lo corta `getModePlanLock('asistente-v2')` en `InterfazV2`: si el
     plan no llega a Pro, la pantalla ni se abre. Por eso acá no hace falta clamp. */
  const aiHelp = esV2 ? true : aiHelpState;
  const [aiMenuOpen, setAiMenuOpen] = useState(false);
  // Qué genera el toggle de arriba del input. En directo decide la tool; con
  // Ayuda de IA viaja como pista al agente.
  // Mismo clamp persistente que `aiHelp`, por el mismo motivo.
  const [outputKind, setOutputKind] = useState<OutputKind>(() => {
    try {
      if (localStorage.getItem('atlas-v2-output-kind') !== 'video') return 'imagen';
      return puedeGenerarVideoEnAsistente(localStorage.getItem('atlas-user-plan'))
        ? 'video'
        : 'imagen';
    } catch {
      return 'imagen';
    }
  });

  /* Segundo clamp, cuando llega el plan REAL.
     El initializer corre contra el cache, que puede estar vacío o viejo. Si el plan que
     llega no alcanza, se apaga lo que haya quedado prendido. Sólo apaga: nunca prende
     nada por su cuenta. */
  useEffect(() => {
    if (!plan) return;
    // En v2 el clamp de la ayuda no corre: `aiHelp` es derivado y apagar `aiHelpState`
    // no cambiaría nada acá, pero sí le dejaría el toggle apagado a v1 al volver.
    if (!esV2 && !puedeUsarAyudaIA(plan)) setAiHelp(false);
    if (!puedeGenerarVideoEnAsistente(plan)) {
      setOutputKind((k) => (k === 'video' ? 'imagen' : k));
    }
  }, [plan, esV2]);

  /**
   * El ÚNICO camino para cambiar imagen↔video — ADR-2026-08-20-crear-desde-cero-se-abre-y-el-candado-baja-a-las-opciones.
   *
   * El toggle se dibuja en CUATRO lugares (dos ramas del composer, el panel expandido
   * y `ConfiguracionMenu`), así que gatear en cada render era garantía de dejar un
   * bypass el día que aparezca el quinto. Se gatea el SETTER: los cuatro reciben esto y
   * `setOutputKind` crudo no sale de este bloque.
   */
  const cambiarOutputKind = useCallback(
    (k: OutputKind) => {
      if (k === 'video') {
        const lock = getOpcionAsistenteLock('video', plan);
        if (lock) {
          setLockOpcion(lock);
          return;
        }
      }
      setOutputKind(k);
    },
    [plan],
  );
  // Formato y duración: el motor los exige y en directo no hay agente que
  // los decida. `atlas-v2-last-format` es la MISMA clave que ya escribe la
  // tool card al generar, así que el default arranca en lo último usado.
  const [formatSlug, setFormatSlug] = useState<string>(() => {
    try {
      // Un formato personalizado se lee por su proporción estándar (ADR-2026-10-20-formatos-personalizados).
      return formatoEstandarDe(localStorage.getItem('atlas-v2-last-format')) || DEFAULT_FORMAT_ID;
    } catch {
      return DEFAULT_FORMAT_ID;
    }
  });
  const [videoDuration, setVideoDuration] = useState<number>(5);
  // Calidad y resolución del video (ruedita). Default: PROFESIONAL a 720p
  // (decisión de producto 2026-08-30). La calidad define el tope del slider
  // (básica 15s / profesional 30s): al bajar de calidad se clampa la
  // duración para que el payload nunca salga fuera de rango.
  const [videoQuality, setVideoQuality] = useState<VideoQuality>('profesional');
  const [videoResolution, setVideoResolution] =
    useState<VideoResolution>('720p');
  const cambiarVideoQuality = useCallback((q: VideoQuality) => {
    setVideoQuality(q);
    setVideoDuration((d) => clampDuracion(d, q));
  }, []);
  // Cuántas imágenes por tanda (solo aplica a Imagen). Vive en el menú de la
  // ruedita, como stepper +/-.
  const [cantidad, setCantidad] = useState<number>(1);
  const [configMenuOpen, setConfigMenuOpen] = useState(false);
  // Sheet de referencias (mobile) — la card única del empty state lo abre.
  const [referenciasOpen, setReferenciasOpen] = useState(false);

  // ── Modo DIRECTO: pantalla de carga tradicional ──────────────────────
  // Sin Ayuda de IA el modo se comporta como el resto de la app: overlay
  // full-screen mientras genera y, al terminar, salto a las imágenes
  // generadas. Con Ayuda de IA NO se usa: ahí la generación es un paso más
  // de la conversación y sacar al usuario del chat lo cortaría.
  const navigate = useNavigate();
  const [showLoadingScreen, setShowLoadingScreen] = useState(false);
  const [completeProgressNow, setCompleteProgressNow] = useState(false);
  // Qué se está generando bajo el overlay — el mensaje y la duración de la
  // barra difieren (el video tarda varios minutos, la imagen ~1).
  const [loadingKind, setLoadingKind] = useState<OutputKind>('imagen');
  // Fallo de la card de video en modo directo (corre tapada por el overlay):
  // la card avisa por evento y acá se baja la pantalla de carga para que el
  // error quede a la vista (sin esto, la barra de video seguiría ~10 min).
  useEffect(() => {
    const onDirectError = () => {
      setShowLoadingScreen(false);
      setCompleteProgressNow(false);
    };
    window.addEventListener('atlas:direct-media-error', onDirectError);
    return () =>
      window.removeEventListener('atlas:direct-media-error', onDirectError);
  }, []);
  const pendingNavRef = useRef<{
    imageIds: (string | number)[];
    companyId: string;
  } | null>(null);

  // Costo del próximo envío en modo directo — se muestra en una pill al lado
  // del botón de generar.
  const directCreditCost = creditsForDirectCall({
    outputKind,
    cantidad,
    durationSeconds: videoDuration,
    videoQuality,
    videoResolution,
  });

  useEffect(() => {
    try {
      // `atlas-v2-ai-help` y `atlas-v2-output-kind` son la memoria de DOS controles que
      // v2 no tiene. Escribirlos desde acá le cambiaría a v1 lo que el usuario dejó
      // elegido allá (la ayuda siempre prendida, el tipo de salida siempre en el
      // default) sin que nadie haya tocado nada.
      if (!esV2) {
        localStorage.setItem('atlas-v2-ai-help', aiHelp ? '1' : '0');
        localStorage.setItem('atlas-v2-output-kind', outputKind);
      }
      localStorage.setItem('atlas-v2-last-format', formatSlug);
    } catch {
      /* noop */
    }
  }, [aiHelp, outputKind, formatSlug, esV2]);

  // La pista del toggle solo viaja si hay un agente escuchando.
  const setOutputKindHint = chat.setOutputKindHint; // estable (useCallback)
  useEffect(() => {
    // En v2 NUNCA viaja: sin switch, `outputKind` no representa ninguna elección del
    // usuario y mandarlo como pista sesgaría al agente hacia imagen aunque le hayan
    // pedido un video con todas las letras. Ahí el tipo de salida sale del texto.
    setOutputKindHint(!esV2 && aiHelp ? outputKind : null);
  }, [esV2, aiHelp, outputKind, setOutputKindHint]);

  // Menú "+" de adjuntos — abierto/cerrado. Mismo state para mobile
  // (bottom sheet) y desktop (popup), el render distingue por
  // breakpoint.
  const [attachOpen, setAttachOpen] = useState<boolean>(false);
  // Vista interna del menú de adjuntos. `root` es la pantalla principal
  // (Subir imagen + Seleccionar producto / modelo / Otras fuentes).
  // `fuentes` es la sub-vista de "Otras fuentes" (Imagen ya generada,
  // Sacar foto, Cargar desde el móvil — stubs por ahora).
  const [attachView, setAttachView] = useState<'root' | 'fuentes'>('root');
  // Reset de la sub-vista al cerrar — si el user entró a `fuentes` y
  // cerró sin elegir nada, la próxima vez que abra el menú arranca de
  // vuelta en `root`.
  useEffect(() => {
    if (!attachOpen) setAttachView('root');
  }, [attachOpen]);
  // Cerrar el menú "+" al tocar/clickear afuera (desktop popup y, redundante
  // con el overlay, mobile sheet). El marcador `data-attach-menu` va en los TRES
  // nodos que forman el menú —el ancla del "+", el popup de v2 colgado del
  // `inputBox` y el sheet mobile—, así un click adentro no lo cierra. `closest`
  // sube por el árbol, así que alcanza con marcarlos a ellos.
  useEffect(() => {
    if (!attachOpen) return;
    const onOutside = (e: Event) => {
      const t = e.target as HTMLElement | null;
      if (t && t.closest('[data-attach-menu]')) return;
      setAttachOpen(false);
    };
    document.addEventListener('mousedown', onOutside);
    document.addEventListener('touchstart', onOutside);
    return () => {
      document.removeEventListener('mousedown', onOutside);
      document.removeEventListener('touchstart', onOutside);
    };
  }, [attachOpen]);
  const closeAttach = () => setAttachOpen(false);

  // ── Adjuntos (menú "+") ─────────────────────────────────────────────
  // Picker abierto desde el menú "+": producto o modelo. La imagen usa un
  // <input type=file> oculto, no un picker.
  const [attachPicker, setAttachPicker] = useState<
    'none' | 'product' | 'model' | 'generated' | 'pose'
  >('none');
  // Catálogo de poses para la fila "Poses" del menú Agregar. Se trae la PRIMERA
  // vez que se abre el picker y queda cacheado en el estado: es público, cambia
  // poco, y traerlo al montar el sheet sería una consulta que la mayoría de las
  // sesiones no usa. Ver ADR-2026-09-08-poses-se-adjuntan-como-imagen-de-referencia.
  const [poses, setPoses] = useState<PoseV2[]>([]);
  const [posesCargadas, setPosesCargadas] = useState(false);
  const [imageUploading, setImageUploading] = useState(false);
  // Adjunto que se está viendo ampliado (lightbox). null = cerrado.
  const [previewAttachment, setPreviewAttachment] =
    useState<ChatAttachment | null>(null);
  const fileInputRef = useRef<HTMLInputElement | null>(null);
  const userProducts = useUserProducts();
  const userModels = useUserModels();
  const { userId, companyId, refreshModels } = useAgentEnv();

  // Subir imagen de referencia: abre el file picker; al elegir, sube al
  // storage y agrega un chip de imagen.
  const onPickImageFile = async (file: File | null) => {
    if (!file || !companyId) return;
    setImageUploading(true);
    try {
      const { storagePath, previewUrl } = await uploadReferenceImage(
        file,
        companyId,
      );
      chat.addAttachment({
        kind: 'image',
        id: `img-${Date.now()}`,
        storagePath,
        previewUrl,
      });
    } catch (e) {
      console.error('[AsistenteMode] subir imagen falló', e);
    } finally {
      setImageUploading(false);
    }
  };

  // Aceptar productos del ProductFullMenu → chips de producto.
  const onAcceptProducts = (ids: string[]) => {
    for (const id of ids) {
      const p = userProducts.find((x) => x.id === id);
      if (!p) continue;
      chat.addAttachment({
        kind: 'product',
        id: `prod-${id}`,
        productId: id,
        name: p.name,
        imageUrl: p.imageUrl,
      });
    }
    setAttachPicker('none');
  };

  // Elegir una o varias imágenes ya generadas → chips de referencia (bucket
  // `generadas`).
  const onAcceptGenerated = (picks: GeneratedPick[]) => {
    picks.forEach((pick, i) => {
      chat.addAttachment({
        kind: 'image',
        id: `gen-${Date.now()}-${i}`,
        storagePath: pick.storagePath,
        previewUrl: pick.previewUrl,
        bucket: pick.bucket,
      });
    });
    setAttachPicker('none');
  };

  // Elegir poses → chips de IMAGEN. Una pose no es un tipo de adjunto propio:
  // es una foto de referencia como cualquier otra, y el agente ya sabe qué hacer
  // con imágenes de referencia. Viven en el bucket público `system` bajo
  // `poses/<filename>`; `previewUrl` usa la URL pública directa (no hace falta
  // firmarla para mostrarla) y `storagePath` + `bucket` son lo que se re-firma al
  // generar. Ver ADR-2026-09-08-poses-se-adjuntan-como-imagen-de-referencia.
  const onAcceptPoses = (ids: string[]) => {
    ids.forEach((id) => {
      const pose = poses.find((p) => p.id === id);
      if (!pose) return;
      chat.addAttachment({
        kind: 'image',
        // El id lleva el de la pose y no un timestamp: adjuntar la misma pose
        // dos veces manda la misma referencia repetida. `addAttachment` deduplica
        // por id, así que con esto la segunda vez no hace nada.
        id: `pose-${pose.id}`,
        storagePath: `poses/${pose.filename}`,
        previewUrl: pose.imageUrl,
        bucket: 'system',
      });
    });
    setAttachPicker('none');
  };

  // Elegir modelo del ModelPickerSheet → chip de modelo (único).
  const onPickModel = (sel: ModelSelection) => {
    chat.addAttachment({
      kind: 'model',
      id: 'model-attachment',
      selection: sel,
      name: modelSelectionLabel(sel),
      imageUrl:
        sel.mode === 'preset'
          ? sel.imageUrl
          : sel.mode === 'upload'
            ? sel.previewUrl
            : null,
    });
    setAttachPicker('none');
  };

  const promptRef = useRef<HTMLTextAreaElement | null>(null);
  // Ref al scroll de mensajes + al último mensaje del user (lo anclamos
  // arriba al enviar, estilo Director de Estudio).
  const messagesScrollRef = useRef<HTMLDivElement | null>(null);
  /** El contenido ADENTRO del scroller. Se observa su alto para seguir al agente
   *  también cuando el contenido crece sin que React re-renderice
   *  (ADR-2026-09-08-el-turno-sigue-al-agente-cuando-no-entra-en-pantalla). */
  const messagesContentRef = useRef<HTMLDivElement | null>(null);
  const latestUserMsgRef = useRef<HTMLDivElement | null>(null);
  // Mientras está "pineado", re-anclamos el último mensaje del user cerca
  // del tope en cada frame (así el contenido crece debajo y queda todo
  // visible). Se suelta cuando el user scrollea al fondo manualmente.
  const pinUserToTopRef = useRef<boolean>(false);

  // Token del run DIRECTO en vuelo. El tramo async de `handleGenerate` sobrevive
  // a cualquier cosa que pase mientras firma las imágenes adjuntas (una ida a la
  // red): si en ese hueco se prende Ayuda de IA o se empieza un chat nuevo, al
  // volver escribiría las referencias viejas y empujaría su tool card sobre una
  // sesión que ya es OTRA — trabajo del modo anterior metido en la nueva, y sus
  // referencias colgadas de los mensajes del agente que vengan después.
  //
  // Va acá y no con el `genToken` de `useV2Chat` porque ese sólo se bumpea en
  // `reset()`, y el caso que importa no resetea: con la sesión vacía
  // (`hasMessages === false`, o sea la primera generación directa) el cambio de
  // modo no pide confirmación ni limpia nada, sólo cambia de modo.
  const directRunRef = useRef(0);

  // ── Menú hamburguesa + historial + consumo ──────────────────────────
  const [menuOpen, setMenuOpen] = useState(false);
  // Vista interna del menú hamburguesa: 'root' (3 opciones) o 'consumo'
  // (página de créditos gastados, dentro del mismo popover).
  const [menuView, setMenuView] = useState<'root' | 'consumo'>('root');
  // Memoria del agente — panel slide-in (mismo patrón que el historial).
  const [memoriaOpen, setMemoriaOpen] = useState(false);
  const [memoriasList, setMemoriasList] = useState<AgentMemory[]>([]);
  const [memoriasLoading, setMemoriasLoading] = useState(false);
  const [historialOpen, setHistorialOpen] = useState(false);
  const [historialChats, setHistorialChats] = useState<AgentChatSummary[]>([]);
  const [historialLoading, setHistorialLoading] = useState(false);
  // Consumo del chat (créditos = agente LLM + imágenes), leído de la DB.
  const [consumoCredits, setConsumoCredits] = useState<number | null>(null);
  const [consumoLoading, setConsumoLoading] = useState(false);
  // Reset de la sub-vista al cerrar el menú.
  useEffect(() => {
    if (!menuOpen) setMenuView('root');
  }, [menuOpen]);
  // Al entrar a la página de consumo, leer el total real de la DB.
  useEffect(() => {
    if (menuView !== 'consumo') return;
    let alive = true;
    setConsumoLoading(true);
    chat.fetchConsumo().then((n) => {
      if (alive) {
        setConsumoCredits(n);
        setConsumoLoading(false);
      }
    });
    return () => {
      alive = false;
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [menuView]);
  /* Borrado del historial — ADR-2026-09-08-borrar-el-historial-de-chats.

     Optimista: la fila se va de la lista al toque y el borrado real viaja atrás. Si la
     base NO borró (RLS, red), la fila VUELVE — `deleteChats` devuelve los ids que
     realmente cayeron, justo para poder distinguir "no se borró" de "no hubo error".
     Sin eso, la lista mentiría hasta el próximo `listChats()`.

     Y si el chat borrado es el que está en pantalla, la sesión se resetea: dejarlo
     abierto es seguir escribiendo en una conversación que ya no existe, y el próximo
     `updateChatMessages` iría contra una fila borrada. */
  const olvidarChatSiEsElAbierto = (ids: string[]) => {
    let abierto: string | null = null;
    try {
      abierto = sessionStorage.getItem(clavesDeStorage().chatId);
    } catch {
      /* noop */
    }
    if (abierto && ids.includes(abierto)) chat.reset();
  };

  /* Qué borrado está esperando confirmación (null = modal cerrado) —
     ADR-2026-09-08-la-confirmacion-de-borrar-es-un-modal-compartido.
     `{ tipo: 'uno' }` trae el chat para poder nombrarlo en el título; `'todos'` no
     necesita nada más que el largo de la lista. */
  const [pedirBorrado, setPedirBorrado] = useState<
    { tipo: 'uno'; chat: AgentChatSummary } | { tipo: 'todos' } | null
  >(null);
  /** Los del usuario, que son los únicos que puede borrar. Sin `userId` la lista
   *  queda vacía: es preferible no ofrecer borrar nada a ofrecer borrar lo ajeno. */
  const misChats = useMemo(
    () => (userId ? historialChats.filter((c) => c.user_id === userId) : []),
    [historialChats, userId],
  );
  const [borrandoChats, setBorrandoChats] = useState(false);

  const confirmarBorrado = async () => {
    if (!pedirBorrado) return;
    /* "Borrar todo" manda SÓLO los chats propios, no toda la lista. El historial
       es por EMPRESA (ver listChats), así que `historialChats` trae también los de
       los compañeros: mandarlos todos vaciaba el historial del equipo entero, y la
       confirmación sólo decía un número, nunca de quién eran
       (ADR-2026-09-11-el-borrado-del-historial-no-cruza-usuarios). */
    const ids =
      pedirBorrado.tipo === 'uno'
        ? [pedirBorrado.chat.id]
        : misChats.map((c) => c.id);
    if (ids.length === 0) {
      setPedirBorrado(null);
      return;
    }
    /* El modal se queda abierto —con el spinner en el botón— hasta que la base
       conteste, y recién ahí se saca la fila. NO es optimista a propósito: el borrado
       es definitivo y sin deshacer, así que sacar la fila antes de tiempo y reponerla
       si falló haría parpadear justo lo que el usuario acaba de confirmar. */
    setBorrandoChats(true);
    const borrados = await deleteChats(ids, userId || '');
    setBorrandoChats(false);
    setPedirBorrado(null);
    if (borrados.length === 0) return;
    setHistorialChats((prev) => prev.filter((c) => !borrados.includes(c.id)));
    olvidarChatSiEsElAbierto(borrados);
  };

  const handleDeleteMemoria = async (id: string) => {
    setMemoriasList((prev) => prev.filter((m) => m.id !== id));
    await deleteMemory(id);
  };
  // Autosize del textarea (mismo patrón que el PromptBar de la home).
  useEffect(() => {
    const el = promptRef.current;
    if (!el) return;
    el.style.height = 'auto';
    el.style.height = `${Math.min(el.scrollHeight, 240)}px`;
    // Pasado el tope, el texto scrollea por dentro; con `overflow-hidden` fijo no se llegaba al resto
    // (ADR-2026-10-31-las-cajas-scrollean-pasado-el-tope).
    el.style.overflowY = el.scrollHeight > 240 ? 'auto' : 'hidden';
  }, [prompt]);

  /** Dónde tiene que quedar el scroll mientras dura el turno.
   *
   *  DOS comportamientos, y el segundo es el que faltaba
   *  (ADR-2026-09-08-el-turno-sigue-al-agente-cuando-no-entra-en-pantalla):
   *
   *   • Mientras la respuesta ENTRA en pantalla, el mensaje del user se queda
   *     anclado arriba. Es la lectura natural: se ve la pregunta y debajo va
   *     apareciendo la respuesta.
   *   • Cuando la respuesta pasa a ser MÁS ALTA que el viewport, el ancla deja
   *     de servir: lo último que escribe el agente —o la card que acaba de
   *     montar— cae abajo del borde y hay que ir a buscarlo a mano. Ahí se sigue
   *     el fondo, que es lo único que garantiza ver lo último.
   *
   *  Devuelve `null` si todavía no se puede calcular (sin contenedor o sin
   *  mensaje del user en pantalla). */
  const objetivoDeScrollDelTurno = (): number | null => {
    const container = messagesScrollRef.current;
    const target = latestUserMsgRef.current;
    if (!container || !target) return null;
    const cRect = container.getBoundingClientRect();
    const tRect = target.getBoundingClientRect();
    const anclaje = Math.max(
      0,
      tRect.top - cRect.top + container.scrollTop - 16,
    );
    const maxScroll = Math.max(
      0,
      container.scrollHeight - container.clientHeight,
    );
    // ¿Lo que va desde el ancla hasta el final entra en la ventana?
    const entra = container.scrollHeight - anclaje <= container.clientHeight;
    return entra ? Math.min(anclaje, maxScroll) : maxScroll;
  };

  /** Cuándo movimos el scroll NOSOTROS por última vez.
   *
   *  Hace falta porque `onScroll` no distingue quién scrolleó, y desde que el
   *  turno puede seguir el fondo, nuestro propio scroll deja `dist ≈ 0` — o sea
   *  que el handler de abajo soltaría el pin al primer frame de seguimiento y se
   *  cortaría solo. Los gestos del usuario NO pasan por acá: tienen sus propios
   *  handlers (`onWheel`/`onTouchMove`), que sueltan el pin al toque. */
  const ultimoScrollPropioRef = useRef(0);

  /** Lleva el scroll a donde manda `objetivoDeScrollDelTurno`. */
  const seguirElTurno = (behavior: ScrollBehavior = 'smooth') => {
    const container = messagesScrollRef.current;
    const objetivo = objetivoDeScrollDelTurno();
    if (!container || objetivo === null) return;
    ultimoScrollPropioRef.current = performance.now();
    container.scrollTo({ top: objetivo, behavior });
  };

  // Auto-scroll. Si hay pin activo (turno en curso), mantenemos el mensaje
  // del user arriba. Sino, seguimos al fondo cuando el user ya estaba cerca.
  useEffect(() => {
    const sc = messagesScrollRef.current;
    if (!sc) return;
    if (pinUserToTopRef.current) {
      seguirElTurno('smooth');
      return;
    }
    const dist = sc.scrollHeight - sc.scrollTop - sc.clientHeight;
    if (dist < 120) {
      sc.scrollTo({ top: sc.scrollHeight, behavior: 'smooth' });
    }
  }, [chat.messages, chat.activeToolCard, chat.streaming]);

  // Loop que re-ancla el mensaje del user arriba mientras el agente trabaja
  // (texto en streaming, cards que aparecen, etc. empujan contenido y
  // pueden moverlo). Se corta al terminar el turno o al soltar el pin.
  useEffect(() => {
    if (!chat.streaming || !pinUserToTopRef.current) return;
    let raf = 0;
    const tick = () => {
      if (!pinUserToTopRef.current) return;
      const container = messagesScrollRef.current;
      const objetivo = objetivoDeScrollDelTurno();
      // La marca va en CADA tick, muevas o no. Si sólo se marcara al mover, en
      // los frames en que el scroll ya está en su lugar la guarda se vencía y un
      // evento de `onScroll` cualquiera —con `dist ≈ 0`, porque justamente
      // estamos al fondo— soltaba el pin y cortaba el seguimiento para siempre.
      ultimoScrollPropioRef.current = performance.now();
      // Asignación directa a `scrollTop`, no `scrollTo({behavior:'smooth'})`:
      // esto corre CADA frame y un scroll suave por frame se pisa a sí mismo.
      // El movimiento ya es suave porque el contenido crece de a poco.
      if (
        container &&
        objetivo !== null &&
        Math.abs(container.scrollTop - objetivo) > 1
      ) {
        container.scrollTop = objetivo;
      }
      raf = requestAnimationFrame(tick);
    };
    raf = requestAnimationFrame(tick);
    return () => cancelAnimationFrame(raf);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [chat.streaming]);

  /* El contenido crece SIN que React re-renderice, y bastante seguido: los
     textareas de las cards hacen autosize en su propio efecto, las imágenes
     reservan alto recién al cargar, los acordeones se abren. Nada de eso pasa por
     `chat.messages`, así que el efecto de auto-scroll de arriba no se entera y la
     última parte del chat queda abajo del borde — que es justo el síntoma que se
     reportó.

     El `ResizeObserver` mira el alto real y vuelve a seguir el turno. Va con
     `'auto'` y no `'smooth'`: puede dispararse varias veces seguidas mientras
     todo se acomoda, y varios scrolls suaves encimados se pisan entre sí. */
  useEffect(() => {
    const contenido = messagesContentRef.current;
    if (!contenido || typeof ResizeObserver === 'undefined') return;
    const ro = new ResizeObserver(() => {
      if (!pinUserToTopRef.current) return;
      seguirElTurno('auto');
    });
    ro.observe(contenido);
    return () => ro.disconnect();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [hasMessages]);

  // Si el user scrollea hasta el fondo, soltamos el pin (quiere seguir el
  // contenido nuevo, no quedarse anclado arriba).
  //
  // El `250ms` de guarda es lo que separa su scroll del NUESTRO: un
  // `scrollTo({behavior:'smooth'})` sigue emitiendo eventos un rato después de
  // llamarlo, y desde que el turno puede seguir el fondo nuestro propio scroll
  // deja `dist ≈ 0` — sin la guarda, el pin se soltaría solo en el primer frame
  // de seguimiento y el turno dejaría de seguir al agente justo cuando más falta
  // hace (ADR-2026-09-08-el-turno-sigue-al-agente-cuando-no-entra-en-pantalla).
  const handleMessagesScroll = () => {
    const sc = messagesScrollRef.current;
    if (!sc) return;
    // Con el turno en curso, `onScroll` NO suelta nada. La regla vieja —"si
    // llegaste al fondo, soltá"— tenía sentido cuando el pin era quedarse
    // ARRIBA: llegar al fondo era decir "quiero seguir lo nuevo". Ahora seguir
    // lo nuevo ES el pin, así que esa condición se cumple sola en cada frame y
    // lo único que hacía era cortarse a sí misma. Quien suelta el turno es el
    // usuario, con un gesto: `onWheel` / `onTouchMove`.
    if (chat.streaming) return;
    if (performance.now() - ultimoScrollPropioRef.current < 250) return;
    const dist = sc.scrollHeight - sc.scrollTop - sc.clientHeight;
    if (dist < 60) pinUserToTopRef.current = false;
  };

  // Soltar el pin ante CUALQUIER scroll manual (touch/wheel), no solo al llegar
  // al fondo. Sin esto, mientras el agente streamea, el RAF de arriba re-fija el
  // scroll cada frame y el usuario NO puede scrollear (rebota a la posición
  // pineada) — y como nunca puede llegar al fondo, la única vía de soltar el pin
  // (`dist < 60`) es inalcanzable. Apenas el user toma control con un gesto, el
  // pin se suelta y el RAF para (chequea pinUserToTopRef.current al inicio del tick).
  const releasePinOnUserScroll = () => {
    pinUserToTopRef.current = false;
  };

  const hasText = prompt.trim().length > 0;
  /** El mic está SIEMPRE que el navegador lo soporte, con la caja vacía o a
   *  medio escribir: dictar es otra forma de escribir, no un reemplazo del
   *  teclado — ADR-2026-09-08-el-mic-no-desaparece-al-escribir. Lo dictado se
   *  SUMA a lo que ya había (ver el `onConfirm` de `DictadoVoz`). */
  const mostrarMic = dictadoDisponible();
  // Agente ya conversando: con Ayuda de IA prendida Y conversación empezada.
  // A partir de acá los controles de generación (ruedita + toggle
  // Imagen/Video) se esconden — quien decide formato, cantidad, duración y
  // si sale imagen o video es el agente, según lo que le pidas por texto.
  const agenteConversando = aiHelp && hasMessages;
  // Mientras el agente está respondiendo o pidiendo una tool, no aceptamos
  // input nuevo — replica el comportamiento del ChatPanelV2.
  // Se puede escribir mientras una PROPUESTA espera: al enviar, la propuesta
  // no se pierde — queda en el chat y se sigue pudiendo generar (detachTurn).
  // Lo que sigue bloqueado es enviar mientras el agente está redactando.
  const sendDisabled = chat.streaming && !chat.activeToolCard;
  /** La encuesta en la que la persona eligió escribir en vez de tocar las opciones. */
  const [escribirEnLugarDeEncuesta, setEscribirEnLugarDeEncuesta] = useState<string | null>(null);
  /** "Crear" está siempre en el composer, pero sólo se puede accionar con al
   *  menos un carácter escrito y sin un turno en vuelo. Sin eso se ve atenuado
   *  — ADR-2026-09-08-crear-esta-siempre-y-se-enciende-con-la-primera-letra.
   *
   *  VA ACÁ, debajo de `sendDisabled`, y no arriba con `hasText`: son dos
   *  `const` del mismo scope, así que leerlo antes de su declaración es un TDZ
   *  —`Cannot access 'sendDisabled' before initialization`— que tira en cada
   *  render. Si alguien mueve `sendDisabled`, esto lo sigue. */
  const puedeCrear = hasText && !sendDisabled;
  /** Ancho del composer (y de la tool card, que ocupa su mismo lugar).
   *
   *  En v2 es `max-w-3xl` — EL MISMO de la columna de mensajes del chat, que
   *  también es `max-w-3xl mx-auto`. Con los 680px de antes la caja quedaba 88px
   *  más angosta que el texto que uno acaba de leer, y el borde no alineaba con
   *  nada. En v1 se queda en 680px: su composer tiene otra fila de controles y
   *  nadie pidió tocarlo — ADR-2026-09-08-el-composer-mide-lo-mismo-que-la-columna-del-chat. */
  const anchoComposer = esV2 ? 'max-w-3xl' : 'max-w-[680px]';
  // Índice del último mensaje del user — para anclarlo arriba.
  const lastUserIdx = (() => {
    for (let i = chat.messages.length - 1; i >= 0; i--) {
      if (chat.messages[i].role === 'user') return i;
    }
    return -1;
  })();

  /** `textoOverride` sirve a las sugerencias del empty state, que mandan su
   *  prompt sin pasar por el textarea: `setPrompt(x)` + `handleGenerate()` no
   *  sirve porque el state todavía no se aplicó cuando corre la segunda línea.
   *
   *  El `typeof === 'string'` NO es paranoia: `handleGenerate` se pasa tal cual
   *  como `onClick` en dos lugares, y ahí React le mete el MouseEvent en el
   *  primer parámetro. */
  const handleGenerate = (textoOverride?: string) => {
    if (sendDisabled) return;
    const trimmed = (typeof textoOverride === 'string'
      ? textoOverride
      : prompt
    ).trim();
    if (!trimmed) return;

    /* ── Palabra clave de PREVIEW ──────────────────────────────────────────
       `/preguntas` monta el cuestionario de muestra (`PREGUNTAS_DEMO`) sin
       pasar por el agente, para poder ver y tocar la card cuando se quiera.
       Va ANTES de todo lo demás —incluidos los backstops de plan— porque no
       genera nada ni gasta créditos: es UI.

       El texto NO se manda al chat como mensaje del usuario: `/preguntas` es
       un comando, y verlo como burbuja ensucia el historial que después lee
       el agente. */
    if (esV2 && /^\/pregunt(a|as|ar)$/i.test(trimmed)) {
      setPrompt('');
      void (async () => {
        const res: any = await chat.pushToolCard('preguntar', PREGUNTAS_DEMO);
        const respuestas: Array<{ pregunta: string; elegidas: string[] }> =
          res?.respuestas ?? [];
        // El eco cierra el loop: muestra EXACTAMENTE lo que le vuelve al
        // agente, que es lo que hay que revisar además de cómo se ve la card.
        chat.pushAssistantText(
          respuestas.length === 0
            ? 'Cuestionario de muestra cerrado sin respuestas.'
            : [
                'Esto es lo que le llega al asistente:',
                ...respuestas.map(
                  (r) => `• ${r.pregunta} → ${r.elegidas.join(', ')}`,
                ),
              ].join('\n'),
        );
      })();
      return;
    }

    /* `/creacion` — misma idea que `/preguntas`: monta la card de elegir pieza
       (últimas generaciones + puertas al historial y al catálogo) sin el
       agente. Sin `message`, la card usa su copia por defecto. */
    if (esV2 && /^\/creaci[oó]n$/i.test(trimmed)) {
      setPrompt('');
      void (async () => {
        const res: any = await chat.pushToolCard('pick_creacion', {
          message: '¿Sobre cuál querés trabajar?',
        });
        chat.pushAssistantText(
          res?.tipo === 'producto'
            ? `Esto es lo que le llega al asistente: producto ${res.product_id}${res.etiqueta ? ` (${res.etiqueta})` : ''}.`
            : res?.tipo === 'creacion'
              ? `Esto es lo que le llega al asistente: imagen generada ${res.image_id}.`
              : 'Card cerrada sin elegir nada.',
        );
      })();
      return;
    }

    /* ── Backstop de plan ──────────────────────────────────────────────────────
       ADR-2026-08-20-crear-desde-cero-se-abre-y-el-candado-baja-a-las-opciones.

       Los candados de arriba son de UI, y en este modo la UI es lo ÚNICO que hay:
       `checkGenerationGuards('agente')` existe en `InterfazV2` pero NINGÚN call site lo
       usa (los dos que hay pasan 'catalogo' o 'video'), o sea que el camino de
       generación del asistente —directo y agente— nunca consultó el plan. Hasta ahora
       no se notaba porque el modo entero estaba bloqueado; al abrirlo, sin esto el
       único freno sería el 402 del backend, o sea después de gastar el viaje.

       Corta LOS DOS caminos, y por eso va antes del `if (aiHelp)`. */
    if (aiHelp) {
      const lock = getOpcionAsistenteLock('ayuda-ia', plan);
      if (lock) {
        setAiHelp(false);
        setLockOpcion(lock);
        return;
      }
    }
    if (outputKind === 'video') {
      const lock = getOpcionAsistenteLock('video', plan);
      if (lock) {
        setOutputKind('imagen');
        setLockOpcion(lock);
        return;
      }
    }

    if (aiHelp) {
      // ── Camino con AGENTE ─────────────────────────────────────────
      // Si hay una propuesta esperando, soltamos ese turno ANTES de mandar:
      // sin esto `sendMessage` se descartaba en silencio (su guard mira
      // `streaming`, que sigue en true mientras la card espera). La propuesta
      // NO se cancela — queda en el chat, y se puede volver y generarla.
      if (chat.activeToolCard) chat.detachTurn();
      chat.sendMessage(trimmed);
      setPrompt('');
      // Anclar el nuevo mensaje del user arriba, con aire para la respuesta.
      pinUserToTopRef.current = true;
      requestAnimationFrame(() => seguirElTurno('smooth'));
      return;
    }

    // ── Camino DIRECTO (ADR-2026-08-19) ─────────────────────────────
    // Sin LLM: se empuja la tool card a mano, mismo mecanismo scripted
    // que "Sorpréndeme" (InterfazV2). NO se llama pushUserMessage, así
    // que no hay burbuja de usuario ni texto de Atlas: el panel queda
    // como una tira de piezas generadas.
    const atts = chat.attachments;
    const { kind, input } = buildDirectToolCall({
      outputKind,
      prompt: trimmed,
      formatSlug,
      cantidad,
      durationSeconds: videoDuration,
      videoQuality,
      videoResolution,
      attachments: atts,
    });
    setPrompt('');

    const runToken = ++directRunRef.current;
    /** ¿Este run sigue siendo el vigente? Ver `directRunRef`. */
    const vigente = () => runToken === directRunRef.current;

    void (async () => {
      try {
        // Las imágenes adjuntas viajan como {source:'adjunto'}, que la card
        // resuelve leyendo getReferenceImages(). Ese ref lo puebla
        // sendMessage, que acá no corre — así que las firmamos nosotros.
        const imageAtts = atts.filter(
          (a): a is Extract<ChatAttachment, { kind: 'image' }> =>
            a.kind === 'image',
        );
        const urls =
          imageAtts.length > 0
            ? await Promise.all(
                imageAtts.map((a) => signReferenceImage(a.storagePath, a.bucket)),
              )
            : [];
        // Firmar es una ida a la red y el usuario pudo cambiar de modo o
        // arrancar de cero mientras tanto. Se corta ANTES de la primera
        // escritura: a partir de acá todo lo que sigue toca la sesión.
        if (!vigente()) return;
        chat.setReferenceImages(urls.filter((u): u is string => !!u));
        chat.clearAttachments();
        // TODO autogenera en directo (imagen y, desde 2026-08-31, también
        // video — el costo ya se confirmó en la pill del composer), así que
        // el overlay de carga tradicional arranca ya para ambos.
        setLoadingKind(outputKind);
        setCompleteProgressNow(false);
        setShowLoadingScreen(true);
        const res: any = await chat.pushToolCard(kind, input);
        // Segundo corte: generar tarda, y el cambio de modo con la sesión vacía
        // no cancela la card (no hay `reset()` que bumpee el genToken del hook).
        // Sin esto, al terminar se navegaría a generadas y se resetearía la
        // sesión del agente en la que el usuario ya está trabajando. Las piezas
        // no se pierden: quedan en el historial de imágenes generadas.
        if (!vigente()) return;
        // Imagen: ids de la tanda. Video: la fila única que escribió el
        // backend (video_id) — misma pantalla de generadas para ambos.
        const ids: (string | number)[] =
          res?.image_ids ?? (res?.video_id ? [res.video_id] : []);
        if (ids.length > 0) {
          pendingNavRef.current = {
            imageIds: ids,
            companyId: res?.company_id || companyId || '',
          };
          // El resultado ya vive en la pantalla de generadas, así que la
          // card que quedó en el stream es puro residuo: en modo directo
          // no hay conversación que la contextualice. Limpiamos para que
          // al volver al modo esté en blanco. (Solo con éxito: si algo
          // falló, la card se queda para mostrar el error.)
          chat.reset();
        }
        // Cierra la barra donde esté: `onCompleted` navega (o, sin ids —
        // video viejo de n8n sin fila —, solo baja el overlay y la card
        // resuelta queda a la vista en el stream).
        setCompleteProgressNow(true);
      } catch (err) {
        console.error('[AsistenteMode] modo directo falló', err);
        // El overlay se baja SOLO si este run sigue siendo el vigente: si ya
        // arrancó otro, bajarlo le apagaría la pantalla de carga al run nuevo.
        if (!vigente()) return;
        // Si el overlay quedó arriba, lo bajamos: sin esto la pantalla de
        // carga se queda para siempre sobre un error que nadie ve.
        setShowLoadingScreen(false);
        setCompleteProgressNow(false);
      }
    })();
  };

  // Cambiar de modo con piezas en pantalla limpia la sesión: lo generado en
  // directo no está en el historial de API del agente, así que dejarlo a la
  // vista produciría un agente que "no ve" lo que el usuario tiene delante.
  // Las piezas no se pierden — siguen en el historial de imágenes generadas.
  const handleToggleAiHelp = (on: boolean) => {
    // Candado de plan ANTES del confirm —
    // ADR-2026-08-20-crear-desde-cero-se-abre-y-el-candado-baja-a-las-opciones.
    // Va primero a propósito: preguntarle "¿empezamos una sesión nueva?" a alguien que
    // no va a poder prender la opción es pedirle que confirme algo que no va a pasar.
    // Sólo se gatea PRENDER: apagarla siempre se puede.
    if (on) {
      const lock = getOpcionAsistenteLock('ayuda-ia', plan);
      if (lock) {
        setLockOpcion(lock);
        return;
      }
    }
    if (hasMessages) {
      const ok = window.confirm(
        on
          ? '¿Activar la Ayuda de IA? Se empieza una sesión nueva. Lo que generaste queda en tu historial.'
          : '¿Desactivar la Ayuda de IA? Se empieza una sesión nueva. Lo que generaste queda en tu historial.',
      );
      if (!ok) return;
      chat.reset();
    }
    // Recién acá, con el cambio ya confirmado: invalida cualquier generación
    // directa en vuelo, porque la sesión que estaba llenando deja de ser la de
    // esta pantalla. Si el usuario canceló el confirm, arriba ya se volvió y el
    // run sigue vivo, que es lo correcto. Ver `directRunRef`.
    directRunRef.current += 1;
    // El menú NO se cierra a propósito: el cambio de modo es sutil (el aura
    // hace fade, el título transiciona de color) y cerrar el menú de golpe
    // rompía esa sensación — además de esconder el switch que se acaba de
    // tocar.
    setAiHelp(on);
  };

  // Mobile: con el teclado abierto fijamos el sheet al área visible EXACTA que
  // reporta visualViewport (top = offsetTop + height = vv.height) en vez de
  // inset-0. Así el input (al pie del flex) queda justo arriba del teclado y NO
  // se corre cuando iOS scrollea el visual viewport para mostrarlo.
  //
  // OJO: NO usamos `window.innerHeight` como base para detectar el teclado — en
  // iOS es mayor que `visualViewport.height` aun SIN teclado (home indicator /
  // toolbar). La base es el MAYOR `vv.height` observado (= viewport sin teclado);
  // si vv.height baja respecto de esa base, hay teclado abierto.
  useEffect(() => {
    const vv = window.visualViewport;
    if (!vv) return;
    let baseline = 0;
    let lastWidth = 0;
    const apply = () => {
      if (window.innerWidth >= 768) {
        setVvStyle({});
        return;
      }
      // Reset de la base al cambiar el ancho (rotación), sino queda stale.
      if (lastWidth !== window.innerWidth) {
        lastWidth = window.innerWidth;
        baseline = vv.height;
      }
      if (vv.height > baseline) baseline = vv.height;
      const kb = Math.max(0, baseline - vv.height);
      // Con el teclado abierto, iOS no solo ENCOGE el visual viewport: también
      // lo SCROLLEA (offsetTop > 0) para meter el input en vista. El sheet es
      // `fixed inset-0` (anclado al layout viewport), así que sin compensar ese
      // offset el contenido se corre hacia arriba — el input queda cortado y
      // aparece una franja vacía entre él y el teclado. Lo fijamos EXACTO al
      // área visible: top = offsetTop, height = vv.height (en Android offsetTop
      // es 0, así que queda top:0 + height = área sobre el teclado). Sin teclado
      // (kb≈0) volvemos a inset-0 (vvStyle vacío).
      setVvStyle(
        kb > 1
          ? {
              top: `${Math.max(0, vv.offsetTop)}px`,
              height: `${vv.height}px`,
              bottom: 'auto',
            }
          : {},
      );
    };
    apply();
    vv.addEventListener('resize', apply);
    vv.addEventListener('scroll', apply);
    window.addEventListener('resize', apply);
    return () => {
      vv.removeEventListener('resize', apply);
      vv.removeEventListener('scroll', apply);
      window.removeEventListener('resize', apply);
    };
  }, []);

  const openHistorial = async () => {
    setMenuOpen(false);
    setHistorialOpen(true);
    setHistorialLoading(true);
    const chats = await listChats();
    setHistorialChats(chats);
    setHistorialLoading(false);
  };

  const openMemoria = async () => {
    setMenuOpen(false);
    setMemoriaOpen(true);
    if (!companyId) {
      setMemoriasList([]);
      return;
    }
    setMemoriasLoading(true);
    const ms = await fetchMemories(companyId);
    setMemoriasList(ms);
    setMemoriasLoading(false);
  };

  const handleNewChat = () => {
    setMenuOpen(false);
    // Misma razón que en el cambio de modo: una generación directa en vuelo
    // no tiene que aterrizar sobre el chat nuevo. Ver `directRunRef`.
    directRunRef.current += 1;
    chat.reset();
  };

  // Contenido del menú "+" — JSX compartido entre el popup desktop y el
  // bottom sheet mobile. Tiene 2 sub-vistas internas (state `attachView`):
  //   • root    — Subir imagen (botón primario), Seleccionar producto /
  //               modelo, Otras fuentes (entra a la sub-vista).
  //   • fuentes — Volver + Imagen ya generada / Sacar foto / Cargar
  //               desde el móvil (los 3 son stubs por ahora).
  /* Menú "Agregar" de v2 — ADR-2026-09-08-el-menu-agregar-de-v2.
     Cinco orígenes en una lista plana, sin sub-vistas: reemplaza al menú de v1
     (que tiene un CTA primario + "otras fuentes" con drill-down). Lo consumen las
     MISMAS dos superficies que el de v1 —el popup de desktop y el bottom sheet de
     mobile—, porque las dos pintan `attachMenuContent`.

     Poses y Videos van deshabilitados: `ChatAttachment` sólo admite `image`,
     `product` y `model`, y el uploader normaliza como imagen. Activarlos no es
     cablear un onClick — hay que sumar el tipo de adjunto y que el agente sepa
     recibirlo. Hasta entonces la fila se ve pero no miente. */
  const menuAgregarContent = (
    <div className="flex flex-col">
      <AgregarMenuItem
        icon={<ImageIcon className="w-[18px] h-[18px]" />}
        label="Imágenes"
        descripcion="Tus generadas en Atlas"
        onClick={() => {
          closeAttach();
          setAttachPicker('generated');
        }}
      />
      <AgregarMenuItem
        icon={<Package className="w-[18px] h-[18px]" />}
        label="Productos"
        descripcion="Del catálogo de tu marca"
        onClick={() => {
          closeAttach();
          setAttachPicker('product');
        }}
      />
      <AgregarMenuItem
        icon={<PersonStanding className="w-[18px] h-[18px]" />}
        label="Poses"
        descripcion="Del catálogo de Atlas"
        onClick={async () => {
          closeAttach();
          setAttachPicker('pose');
          // Fetch perezoso: sólo la primera vez. El sheet se abre igual y
          // muestra su grilla vacía por el instante que tarda.
          if (!posesCargadas) {
            const todas = await fetchAllPoses();
            setPoses(todas);
            setPosesCargadas(true);
          }
        }}
      />
      <AgregarMenuItem
        icon={<Paperclip className="w-[18px] h-[18px]" />}
        label="Archivos"
        descripcion="Subir desde tu computadora"
        onClick={() => {
          closeAttach();
          fileInputRef.current?.click();
        }}
      />
      {/* MAQUETA — pedida así a propósito, ver `artificial` en `AgregarMenuItem`
          y ADR-2026-09-08-videos-habilitado-de-mentira. Se ve habilitada; el
          click sólo cierra el menú, porque no hay a dónde llevarlo: un video no
          puede viajar como adjunto todavía. Sacar cuando Videos ande de verdad. */}
      <AgregarMenuItem
        icon={<Video className="w-[18px] h-[18px]" />}
        label="Videos"
        descripcion="Tus generados en Atlas"
        artificial
        onClick={closeAttach}
      />
    </div>
  );

  const attachMenuContent = esV2 ? (
    menuAgregarContent
  ) : attachView === 'root' ? (
      <div className="flex flex-col gap-1">
        {/* Subir imagen — CTA primario con bg brand-primary, sin chevron.
            Es la acción más común así que rompe el patrón de fila gris. */}
        <button
          type="button"
          onClick={() => {
            closeAttach();
            fileInputRef.current?.click();
          }}
          className="w-full inline-flex items-center justify-center gap-2 py-3 mb-1 rounded-xl bg-brand-primary hover:bg-brand-primary-hover text-white text-[14.5px] font-semibold shadow-sm transition-colors"
        >
          <Upload className="w-4 h-4" />
          Añadir imagen
        </button>
        {/* Las demás opciones (no-imagen): en mobile van agrupadas en un
            container tipo lista con divisores; en desktop (`md:contents`) el
            wrapper desaparece y las filas fluyen como antes en el popup. */}
        <div className="mt-1 rounded-2xl border border-black/[0.06] divide-y divide-black/[0.05] overflow-hidden md:mt-0 md:contents md:rounded-none md:border-0 md:divide-y-0">
          <AttachMenuItem
            icon={<Package className="w-4 h-4" />}
            label="Añadir producto"
            onClick={() => {
              closeAttach();
              setAttachPicker('product');
            }}
          />
          <AttachMenuItem
            icon={<User className="w-4 h-4" />}
            label={`Añadir ${palabraDeModeloActual().singular}`}
            onClick={() => {
              closeAttach();
              setAttachPicker('model');
            }}
          />
          <AttachMenuItem
            icon={<Layers className="w-4 h-4" />}
            label="Añadir desde otras fuentes"
            onClick={() => setAttachView('fuentes')}
          />
        </div>
      </div>
    ) : (
      <div className="flex flex-col gap-1">
        {/* Header "Volver" — link azul a la izquierda para regresar al
            root del menú sin cerrar el popup/sheet. */}
        <button
          type="button"
          onClick={() => setAttachView('root')}
          className="self-start inline-flex items-center gap-0.5 px-2 py-2 text-[13.5px] text-brand-primary hover:opacity-75 transition-opacity"
        >
          <ChevronLeft className="w-4 h-4" />
          Volver
        </button>
        {/* Mismo tratamiento que el root: en mobile las opciones van en un
            container tipo lista con divisores; en desktop (`md:contents`) el
            wrapper desaparece y fluyen como filas del popup. */}
        <div className="mt-1 rounded-2xl border border-black/[0.06] divide-y divide-black/[0.05] overflow-hidden md:mt-0 md:contents md:rounded-none md:border-0 md:divide-y-0">
          <AttachMenuItem
            icon={<ImageIcon className="w-4 h-4" />}
            label="Añadir imagen ya generada"
            onClick={() => {
              closeAttach();
              setAttachPicker('generated');
            }}
          />
          {/* "Añadir desde el móvil" — solo en escritorio (en mobile ya estás
              en el teléfono, no tiene sentido). */}
          <div className="hidden md:block">
            <AttachMenuItem
              icon={<Smartphone className="w-4 h-4" />}
              label="Añadir desde el móvil"
              onClick={closeAttach}
            />
          </div>
        </div>
      </div>
    );

  // Contenido del menú hamburguesa. 'root' = 3 opciones; 'consumo' = página
  // de créditos gastados (dentro del mismo popover, con botón Volver).
  const menuContent =
    menuView === 'consumo' ? (
      <div className="flex flex-col">
        <button
          type="button"
          onClick={() => setMenuView('root')}
          className="self-start inline-flex items-center gap-0.5 px-2 py-2 text-[13.5px] text-brand-primary hover:opacity-75 transition-opacity"
        >
          <ChevronLeft className="w-4 h-4" />
          Volver
        </button>
        <div className="px-3 pb-3 pt-1">
          <p className="text-[11px] uppercase tracking-wide font-medium text-[#3C3C43]/55">
            Consumo de este chat
          </p>
          <div className="flex items-center gap-2 mt-2">
            <img
              src="/images/token_icon.png"
              alt=""
              aria-hidden="true"
              className="w-6 h-6 object-contain"
            />
            <span className="text-[26px] font-bold text-brand-primary leading-none">
              {consumoLoading ? '…' : (consumoCredits ?? 0)}
            </span>
            <span className="text-[13px] font-medium text-neutral-500">
              {(consumoCredits ?? 0) === 1 ? 'crédito' : 'créditos'}
            </span>
          </div>
          <p className="text-[12.5px] text-[#3C3C43]/55 mt-1.5 leading-snug">
            Incluye el costo del asistente y las imágenes generadas en esta
            conversación.
          </p>
        </div>
      </div>
    ) : (
      <div className="flex flex-col gap-1">
        {/* "Nuevo chat" sólo en v1: en v2 tiene botón propio en las DOS
            superficies (esquina del encabezado en desktop, pill del chrome en
            mobile), así que acá sería una segunda puerta a lo mismo. */}
        {!esV2 && (
          <AttachMenuItem
            icon={<Plus className="w-4 h-4" />}
            label="Nuevo chat"
            onClick={handleNewChat}
          />
        )}
        <AttachMenuItem
          icon={<Coins className="w-4 h-4" />}
          label="Consumo"
          onClick={() => setMenuView('consumo')}
        />
        <AttachMenuItem
          icon={<Brain className="w-4 h-4" />}
          label="Memoria"
          onClick={openMemoria}
        />
        <AttachMenuItem
          icon={<History className="w-4 h-4" />}
          label="Historial"
          onClick={openHistorial}
        />
      </div>
    );

  // Las listas del historial y de la memoria viven en `PanelesChat` desde que las
  // comparte con el Chat (ADR-2026-09-08-el-chat-toma-el-motor-del-asistente): acá
  // sólo se les pasa lo cargado y qué hacer al tocar.
  const historialListContent = (
    <ListaHistorialChats
      chats={historialChats}
      cargando={historialLoading}
      onElegir={(id) => {
        setHistorialOpen(false);
        chat.loadChatById(id);
      }}
      // Sólo en v2, y sólo los propios: la lista es por empresa.
      {...(esV2
        ? {
            onBorrar: (c: AgentChatSummary) =>
              setPedirBorrado({ tipo: 'uno', chat: c }),
            puedeBorrar: (c: AgentChatSummary) => c.user_id === userId,
          }
        : {})}
    />
  );

  /* "Borrar todo" del historial — v2. Definido una vez porque el panel tiene DOS
     encabezados (el sheet de mobile y el panel lateral de desktop) y son copias.
     Sólo con chats PROPIOS en la lista: el historial es por empresa y nadie borra los
     de un compañero (ADR-2026-09-11-el-borrado-del-historial-no-cruza-usuarios). */
  const historialBorrarTodo = esV2 && misChats.length > 0 && (
    <button
      type="button"
      onClick={() => setPedirBorrado({ tipo: 'todos' })}
      className="inline-flex items-center gap-1.5 h-8 px-3 rounded-full text-[13px] font-medium text-[#3C3C43]/60 hover:text-red-600 hover:bg-red-50 transition-colors"
    >
      <Trash2 className="w-3.5 h-3.5" />
      Borrar todo
    </button>
  );

  const memoriaListContent = (
    <ListaMemorias
      memorias={memoriasList}
      cargando={memoriasLoading}
      onBorrar={handleDeleteMemoria}
    />
  );

  // Portal a `document.body` para escapar de cualquier stacking context
  // creado por ancestros de InterfazV2 (transform/filter/etc.). Sin esto
  // el `z-[201]` queda atrapado en el árbol y el chrome del Sidebar
  // mobile (z-50) termina pintando por encima del sheet.
  return ReactDOM.createPortal(
    <>
      {/* Backdrop — en mobile invisible (el sheet ocupa toda la pantalla);
          en desktop arranca después del sidebar para no oscurecerlo. El
          sidebar queda completamente clickeable y con su look normal.
          z-30 queda debajo del chrome mobile (z-50) para que pills back /
          créditos / menú sigan visibles arriba. */}
      <div
        className={`fixed inset-0 md:left-[var(--sidebar-w,0px)] z-30 bg-black/0 md:bg-black/30 md:rounded-tl-[26px] md:rounded-bl-[26px] ${MARCO_PANTALLA} transition-opacity duration-300 ${
          animated ? 'opacity-100' : 'opacity-0'
        }`}
        style={
          { '--sidebar-w': `${sidebarWidth}px` } as React.CSSProperties
        }
        onClick={onClose}
      />
      {/* Sheet container.
          • Mobile  → fixed inset-0, slide-up desde abajo. z-40 mantiene
            el sheet POR DEBAJO del chrome (z-50) del Sidebar para que el
            user siga viendo los pills de tokens / menú arriba.
          • Desktop → fixed con left=sidebarWidth, slide desde la derecha
            (mismo patrón que PresetDetailSheet). */}
      <div
        className={`fixed inset-0 z-40 bg-surface-mobile md:bg-surface-content flex flex-col transition-[transform,opacity] duration-300 ease-out md:left-[var(--sidebar-w,0px)] md:rounded-tl-[26px] md:rounded-bl-[26px] md:overflow-hidden ${MARCO_PANTALLA} ${BORDE_PANTALLA} ${
          animated
            ? 'translate-y-0 opacity-100'
            : // Mobile → slide-up desde abajo (sheet). Desktop → entrada
              // sutil estilo `mode-swap`: fade + 4px de translateY (mismo
              // lenguaje que el resto de los modos), sin slide horizontal.
              'translate-y-full opacity-100 md:translate-y-1 md:opacity-0'
        }`}
        style={
          // CSS var con el ancho del sidebar — sólo aplica en md+ vía la
          // clase `md:left-[var(--sidebar-w)]` de arriba. En mobile vvStyle
          // suma un padding-bottom = alto del teclado (el sheet sigue
          // inset-0); en md+ vvStyle está vacío.
          {
            '--sidebar-w': `${sidebarWidth}px`,
            ...vvStyle,
          } as React.CSSProperties
        }
      >
        {/* Header — solo visible en desktop con "Volver" texto (mismo
            patrón que el PresetDetailSheet). En mobile el chrome del
            Sidebar ya tiene su back chevron pill (vía mobileBackOverride
            seteado por el useEffect del padre), así que no duplicamos el
            botón acá. */}
        {/* Mismo encabezado que las otras pantallas de modo
            (ADR-2026-08-03-chrome-de-pantalla-compartido). Este modo tiene UNA sola
            pantalla —no hay pasos que encadenar—, así que son dos niveles y el
            asistente es la hoja. `hidden md:flex` lo sigue escondiendo en mobile,
            donde el chrome del Sidebar ya trae su propio back.

            El nombre sale de `nombreCortoModo` (o sea de `COPY_TIRA`) y no de una
            constante acá: es la misma tabla del carrusel de la home, la sidebar y
            el sheet de Generar, así que el modo se llama igual en las cuatro
            superficies — ADR-2026-08-03-un-solo-nombre-por-modo. */}
        {/* En v2 el encabezado va SIEMPRE, también en el empty state: ahora
            cuelga los dos botones de esquina (nuevo chat / historial), así que
            ya no es la franja de ~36px en blanco que antes empujaba hacia abajo
            el bloque centrado. */}
        <EncabezadoPantalla
          className="hidden md:flex items-center justify-between md:px-8 md:pt-6"
          // v2 va SIN breadcrumb. La vuelta al inicio sigue existiendo por la sidebar
          // (desktop) y por el back del chrome del Sidebar (mobile), así que no queda
          // nadie encerrado; lo que se pierde es el rótulo del modo en pantalla.
          migas={
            esV2
              ? []
              : [
                  { label: 'Inicio', onClick: onClose },
                  { label: nombreCortoModo('asistente', 'Asistente') },
                ]
          }
          // Esquina izquierda. `respaldo` es el slot que el encabezado dibuja
          // cuando no hay migas — en otras pantallas es el "Volver"; acá, en v2
          // (la única variante sin breadcrumb), es "Nuevo chat". En v1 se ignora
          // solo: con migas, `contenido` son las migas.
          respaldo={
            esV2 ? (
              <button
                type="button"
                onClick={handleNewChat}
                className={BOTON_ESQUINA}
              >
                <SquarePen className="w-4 h-4" strokeWidth={2.25} />
                Crear nuevo chat
              </button>
            ) : undefined
          }
        >
          {/* Acciones de la derecha. En v2, el botón de historial — gemelo del de
              nuevo chat, que va a la izquierda vía `respaldo`.

              En v1 sigue el pill "Nuevo chat" de siempre: allá la izquierda la
              ocupa el breadcrumb, así que el par de esquinas no entra. Solo con
              algo en pantalla — en el empty state no hay nada que limpiar. */}
          {esV2 ? (
            <div className="flex items-center gap-2">
              <button
                type="button"
                onClick={openHistorial}
                className={BOTON_ESQUINA}
              >
                <History className="w-4 h-4" strokeWidth={2.25} />
                Historial de chats
              </button>
              {/* Consumo y Memoria. v2 sacó el popover que los tenía —pero lo que
                  sobraba era que colgara del COMPOSER, no las herramientas en sí
                  (ADR-2026-09-11-consumo-y-memoria-vuelven-a-tener-entrada)—, así que
                  el mismo `menuContent` se reancla acá, donde ya viven las otras
                  acciones del chat. Sin rótulo: tres pills con texto no entran al
                  lado del breadcrumb-menos encabezado. */}
              <div className="relative">
                <button
                  type="button"
                  onClick={() => setMenuOpen((v) => !v)}
                  aria-label="Más opciones del chat"
                  aria-expanded={menuOpen}
                  className="inline-flex items-center justify-center w-9 h-9 rounded-full bg-neutral-100 hover:bg-neutral-200 text-[rgb(var(--brand-black-rgb)_/_0.9)] transition-colors"
                >
                  <MoreHorizontal className="w-4 h-4" strokeWidth={2.25} />
                </button>
                {menuOpen && (
                  <>
                    <div
                      className="hidden md:block fixed inset-0 z-[55]"
                      onClick={() => setMenuOpen(false)}
                    />
                    {/* Abre hacia ABAJO: acá el ancla está arriba de todo, al
                        revés que en v1 (el popover del composer, al pie). */}
                    <div className="hidden md:block absolute top-full right-0 mt-2 w-[250px] bg-white border border-black/[0.06] rounded-2xl shadow-xl p-2 z-[56]">
                      {menuContent}
                    </div>
                  </>
                )}
              </div>
            </div>
          ) : (
            hasMessages && (
              <button
                type="button"
                onClick={handleNewChat}
                className="inline-flex items-center gap-1.5 py-2 px-4 rounded-full bg-white border border-black/[0.06] shadow-sm text-[13.5px] font-medium text-[rgb(var(--brand-black-rgb)_/_0.9)] hover:border-brand-primary/30 transition-colors"
              >
                <SquarePen className="w-4 h-4" strokeWidth={2.25} />
                Nuevo chat
              </button>
            )
          )}
        </EncabezadoPantalla>

        {/* Nuevo chat e Historial — MOBILE, pegados a la derecha del back del
            chrome del Sidebar. Las medidas replican ese chrome para quedar
            alineados: fila de h-14 con px-4 y pills de w-12, o sea que el back
            ocupa 16→64px, "nuevo chat" arranca en 72px (4.5rem) y el historial
            en 128px (8rem), con los mismos 8px de aire entre pills. El top
            centra los 48px del botón en los 56px de la fila: (56−48)/2 = 4px.

            En v2 los DOS van siempre, igual que en el encabezado de desktop
            (`respaldo` + acciones, ninguno gateado por `hasMessages`). Que
            aparezcan y desaparezcan según haya mensajes los haría saltar de
            lugar justo al mandar el primero. En v1 "nuevo chat" conserva su
            gate y el historial no va acá: allá se llega por el popover
            "Opciones del chat", que en v2 no existe
            (ADR-2026-09-11-el-historial-tiene-entrada-en-mobile). */}
        {(esV2 || hasMessages) && (
          <button
            type="button"
            onClick={handleNewChat}
            aria-label="Nuevo chat"
            className="md:hidden absolute left-[4.5rem] z-50 w-12 h-12 rounded-full bg-white/95 backdrop-blur-md shadow-md ring-1 ring-black/[0.04] text-brand-black/85 inline-flex items-center justify-center active:opacity-50 transition-all"
            style={{ top: 'calc(env(safe-area-inset-top, 0px) + 0.25rem)' }}
          >
            <SquarePen size={22} strokeWidth={2} />
          </button>
        )}
        {esV2 && (
          <button
            type="button"
            onClick={() => setMenuOpen((v) => !v)}
            aria-label="Opciones del chat"
            aria-expanded={menuOpen}
            className="md:hidden absolute left-[8rem] z-50 w-12 h-12 rounded-full bg-white/95 backdrop-blur-md shadow-md ring-1 ring-black/[0.04] text-brand-black/85 inline-flex items-center justify-center active:opacity-50 transition-all"
            style={{ top: 'calc(env(safe-area-inset-top, 0px) + 0.25rem)' }}
          >
            <MoreHorizontal size={22} strokeWidth={2} />
          </button>
        )}

        {/* Contenido principal. Layout flex-col que se reparte entre
            zona scrolleable (título/empty O mensajes/active) arriba y
            zona fija (toggle + input, o la tool card) abajo. En mobile
            sumamos `safe-area-inset-top + 3.5rem` (alto del chrome del
            Sidebar) para que el primer elemento no quede debajo del
            back pill / créditos / menú. */}
        <div className="relative flex-1 min-h-0 flex flex-col pt-[calc(env(safe-area-inset-top,0px)+3.5rem)] md:pt-0">
          {/* El aura (cluster de blobs azules con blur) se quitó: el fondo del
              sheet queda limpio en ambos estados. Con ella se fue también su
              único efecto colateral — el título ya no puede ir en blanco, así
              que va siempre en brand-black (ADR-2026-09-08-asistente-v2-sin-aura-ni-cards).
              El toggle `aiHelp` sigue existiendo: sólo perdió su señal visual. */}

          {/* Helper JSX: input container compartido entre empty y active.
              No es un sub-componente — vive acá adentro para tener acceso
              a `prompt`, `attachOpen`, refs y handlers sin prop drilling. */}
          {(() => {
            // Fila de chips de adjuntos (imagen / producto / modelo) que el
            // user enganchó desde el menú "+". Se muestra arriba del textarea
            // y se manda como contexto con el próximo mensaje.
            const hasChips = chat.attachments.length > 0 || imageUploading;
            const chipsRow = hasChips ? (
              <div className="flex flex-wrap items-center gap-2 px-3 pt-3">
                {chat.attachments.map((a) => {
                  // `texto` no tiene foto (es un adjunto del Chat; el asistente no lo
                  // crea, pero el tipo es compartido).
                  const thumb =
                    miniaturaDeAdjunto(a);
                  return (
                    <button
                      key={a.id}
                      type="button"
                      onClick={() => setPreviewAttachment(a)}
                      className="group relative w-16 h-16 rounded-xl overflow-hidden bg-neutral-100 border border-black/[0.06] cursor-pointer"
                    >
                      {thumb ? (
                        <img
                          src={thumb}
                          alt={a.kind === 'image' ? 'Imagen' : a.name}
                          className="w-full h-full object-cover"
                        />
                      ) : (
                        <span className="w-full h-full inline-flex items-center justify-center text-brand-primary">
                          {a.kind === 'model' ? (
                            <User className="w-6 h-6" />
                          ) : (
                            <ImageIcon className="w-6 h-6" />
                          )}
                        </span>
                      )}
                      {/* Botón cerrar dentro de la imagen, esquina superior
                          derecha. Mobile: siempre visible. Desktop: al hover. */}
                      <span
                        role="button"
                        tabIndex={-1}
                        onClick={(e) => {
                          e.stopPropagation();
                          chat.removeAttachment(a.id);
                        }}
                        aria-label="Quitar adjunto"
                        // Siempre visible, también en desktop: escondida hasta
                        // el hover, no había forma de saber que el adjunto se
                        // podía sacar (y en touch el hover no existe).
                        className="absolute top-1 right-1 inline-flex items-center justify-center w-5 h-5 rounded-full bg-black/55 hover:bg-black/75 text-white shadow-sm transition-colors"
                      >
                        <X className="w-3 h-3" strokeWidth={2.5} />
                      </span>
                    </button>
                  );
                })}
                {imageUploading && (
                  <div className="w-16 h-16 rounded-xl bg-neutral-100 border border-black/[0.06] inline-flex items-center justify-center">
                    <span className="w-4 h-4 rounded-full border-2 border-brand-primary/30 border-t-brand-primary animate-spin" />
                  </div>
                )}
              </div>
            ) : null;

            /* Alto de la caja en v2. Antes, con el prompt vacío, el composer
               colapsaba a UNA fila con forma de pill
               (ADR-2026-09-08-el-composer-de-v2-se-abre-al-escribir); eso se cae
               acá: la caja tiene un alto mínimo propio y muestra siempre el
               textarea arriba y la fila de controles abajo, que es lo que deja
               ver formato y cantidad sin abrir nada
               (ADR-2026-09-08-formato-y-cantidad-inline-en-el-composer).

               Sólo en el EMPTY STATE: con la conversación andando el composer va
               anclado al pie del sheet y 200px de caja se los comería al chat. */
            const composerAlto = esV2 && !hasMessages;

            const inputBox = (
              <div
                // Un solo trazo, gris medio: el borde casi transparente
                // (black/6) más el `ring` azul de marca se leían como dos
                // líneas y teñían de azul toda la caja.
                className={`relative w-full ${anchoComposer} bg-white border border-neutral-300 shadow-sm ${
                  esV2 ? 'rounded-3xl' : 'rounded-2xl'
                } ${
                  // Con el foco puesto, el trazo gris se convierte en un aro de
                  // gradiente que gira con una línea blanca recorriéndolo
                  // (`.composer-borde-vivo` en index.css). Sólo en v2: en v1 el
                  // composer comparte pantalla con el toggle y el menú de Ayuda de
                  // IA, y el borde animado ahí compite con todo eso.
                  esV2 && inputFocused ? 'composer-borde-vivo' : ''
                } ${
                  // 200px: entra el textarea con aire de párrafo (~5 líneas) y la
                  // fila de controles abajo sin que la caja se sienta un input de
                  // una línea. `flex-col` + el `flex-1` del textarea reparten ese
                  // alto: el área de escritura se queda con todo lo que sobra.
                  composerAlto ? 'flex flex-col min-h-[200px]' : ''
                }`}
              >
                {dictando ? (
                  <DictadoVoz
                    onCancel={() => setDictando(false)}
                    onConfirm={(t) => {
                      setDictando(false);
                      // SUMA, no pisa. Desde que el mic está también con la caja
                      // a medio escribir, un `setPrompt(t)` pelado le borraba al
                      // usuario lo que había tipeado — y el dictado tapa el
                      // textarea mientras corre, así que ni lo veía desaparecer.
                      setPrompt((prev) => {
                        const antes = prev.trimEnd();
                        return antes ? `${antes} ${t}` : t;
                      });
                      // Foco al textarea ya remontado, con el texto listo
                      // para retocar o mandar.
                      window.setTimeout(() => promptRef.current?.focus(), 0);
                    }}
                  />
                ) : (
                <>
                <div
                  className={`px-5 pt-4 ${
                    // Con la caja de alto fijo, el área de escritura se come todo
                    // el sobrante: así el click cae en el textarea casi en toda la
                    // caja y la fila de controles queda pegada abajo.
                    composerAlto ? 'flex-1 flex' : ''
                  }`}
                >
                  <textarea
                    ref={promptRef}
                    value={prompt}
                    onChange={(e) => setPrompt(e.target.value)}
                    onFocus={() => setInputFocused(true)}
                    onBlur={() => setInputFocused(false)}
                    onKeyDown={(e) => {
                      // Enter (sin Shift) envía. Shift+Enter = salto de línea.
                      if (e.key === 'Enter' && !e.shiftKey) {
                        e.preventDefault();
                        handleGenerate();
                      }
                    }}
                    placeholder={
                      hasMessages
                        ? 'Pedile algo a Atlas…'
                        : 'Describí lo que querés generar…'
                    }
                    rows={1}
                    disabled={chat.streaming}
                    // `min-h` de dos líneas en TODOS los breakpoints: la caja
                    // arranca con aire para escribir en vez de una sola línea
                    // apretada (desktop se sumó el 2026-08-30; antes tenía
                    // md:min-h-0). Va como min-height y no como `rows` porque
                    // el autosize escribe `style.height` inline — min-height
                    // igual gana. 15.5px × leading-snug (1.375) ≈ 21.3px/línea.
                    className="w-full resize-none bg-transparent border-none outline-none text-[15.5px] text-brand-black/90 placeholder:text-[#3C3C43]/35 leading-snug overflow-y-hidden disabled:cursor-not-allowed min-h-[43px]"
                  />
                </div>
                {/* Adjuntos del menú "+", DEBAJO del textarea: la caja se lee
                    de arriba abajo como lo que va a pasar — primero lo que
                    escribís, después lo que le mandás con eso, y al pie los
                    controles. Arriba del texto empujaban el cursor hacia el
                    medio de la caja y el placeholder quedaba flotando.

                    En MOBILE + empty state se ocultan: ahí los adjuntos ya se
                    ven en la card de referencias y se mostrarían dos veces. En
                    desktop y en el estado activo (donde esa card no existe)
                    los chips son el único lugar. */}
                {chipsRow && (
                  <div className={hasMessages ? '' : 'hidden md:block'}>
                    {chipsRow}
                  </div>
                )}
                {/* Row inferior: [⚙] ([+] solo chateando con IA) … [Ayuda de IA]
                    [créditos] [↑]. El posicionamiento por breakpoint se logra
                    con los `ml-auto`.
                    OJO: NADA de `overflow-x-auto` acá. Esta fila contiene los
                    triggers de TRES popovers (⚙, +, Ayuda de IA) y overflow en
                    cualquier eje crea contexto de clipping: los menús se
                    recortan y parecen "no funcionar". Si la fila no entra, hay
                    que sacar controles, no scrollearla. */}
                <div className="flex items-center gap-2 px-3 pb-3 pt-2">
                  {/* Ajustes de la generación. Se ESCONDEN una vez que el agente
                      está conversando: ahí el formato, la cantidad y la duración
                      las decide él según lo que le pidas, así que dejarlos
                      visibles prometía un control que no manda.

                      En v2 + imagen van INLINE (formato y cantidad a la vista, sin
                      abrir nada — ADR-2026-09-08-formato-y-cantidad-inline-en-el-composer).
                      El resto sigue con la ruedita: en video los ajustes son cuatro
                      (formato, calidad, resolución, duración, con slider incluido) y
                      no entran en la fila; y v1 comparte la fila con el toggle
                      Imagen/Video y el menú de Ayuda de IA, donde tampoco hay lugar. */}
                  {!agenteConversando && esV2 && outputKind === 'imagen' && (
                    <>
                      <FormatoInline
                        formatSlug={formatSlug}
                        onChangeFormat={setFormatSlug}
                      />
                      <CantidadInline
                        cantidad={cantidad}
                        onChangeCantidad={setCantidad}
                      />
                    </>
                  )}
                  {!agenteConversando && !(esV2 && outputKind === 'imagen') && (
                    <ConfiguracionMenu
                      outputKind={outputKind}
                      onChangeOutputKind={cambiarOutputKind}
                      // La ruedita repite el toggle Imagen|Video en mobile (donde el de
                      // arriba del input no existe). En v2 esa fila es el mismo control
                      // que se sacó, así que también se va — sino quedaba el bypass.
                      mostrarOutputKind={!esV2}
                      formatSlug={formatSlug}
                      onChangeFormat={setFormatSlug}
                      cantidad={cantidad}
                      onChangeCantidad={setCantidad}
                      durationSeconds={videoDuration}
                      onChangeDuration={setVideoDuration}
                      videoQuality={videoQuality}
                      onChangeVideoQuality={cambiarVideoQuality}
                      videoResolution={videoResolution}
                      onChangeVideoResolution={setVideoResolution}
                      open={configMenuOpen}
                      onOpenChange={setConfigMenuOpen}
                    />
                  )}
                  {/* Opciones del chat (Nuevo chat / Consumo / Memoria /
                      Historial). Antes vivían en una hamburguesa flotante
                      arriba a la derecha; ahora viven acá, y SOLO con Ayuda
                      de IA: son todas nociones del chat (historial de chats,
                      memoria del agente, créditos de la conversación) que en
                      modo directo no significan nada. En v2 no va: el chat
                      ya no expone estas opciones desde el composer. */}
                  {aiHelp && !esV2 && (
                    <div className="relative shrink-0">
                      <button
                        type="button"
                        onClick={() => setMenuOpen((v) => !v)}
                        aria-label="Opciones del chat"
                        className="inline-flex items-center justify-center w-9 h-9 rounded-full bg-neutral-100 hover:bg-neutral-200 text-[rgb(var(--brand-black-rgb)_/_0.7)] transition-colors"
                      >
                        <MessageSquare className="w-4 h-4" strokeWidth={2.25} />
                      </button>
                      {menuOpen && (
                        <>
                          <div
                            className="fixed inset-0 z-[55]"
                            onClick={() => setMenuOpen(false)}
                          />
                          {/* Abre hacia ARRIBA: la fila vive al pie del sheet. */}
                          <div className="absolute bottom-full left-0 mb-2 w-[250px] bg-white border border-black/[0.06] rounded-2xl shadow-xl p-2 z-[56]">
                            {menuContent}
                          </div>
                        </>
                      )}
                    </div>
                  )}
                  {/* Adjuntar. En v1 es un "+" pelado y SOLO cuando se está
                      chateando con Ayuda de IA (en modo directo los adjuntos entran
                      por las cards del empty state y acá el botón sería ruido).

                      En v2 es "+ Agregar", SIEMPRE visible y primero de la fila
                      (ADR-2026-09-08-el-menu-agregar-de-v2): es la puerta a los cinco
                      orígenes, así que no puede depender de que ya haya conversación.
                      `order-first` en vez de mover el JSX porque este bloque lo
                      comparten las dos variantes y en v1 el "+" va después de ⚙ y 💬. */}
                  {(esV2 || (aiHelp && hasMessages)) && (
                    <div
                      className={`relative shrink-0 ${esV2 ? 'order-first' : ''}`}
                      data-attach-menu
                    >
                      <button
                        type="button"
                        onClick={() => setAttachOpen((v) => !v)}
                        aria-label="Agregar al chat"
                        aria-expanded={esV2 ? attachOpen : undefined}
                        className={`inline-flex items-center justify-center gap-1.5 h-9 rounded-full transition-colors ${
                          // El rótulo sólo en desktop, igual que ⚙: en mobile la fila
                          // colapsada comparte 390px con el placeholder y el mic.
                          esV2 ? 'w-9 md:w-auto md:px-3.5' : 'w-9'
                        } ${
                          attachOpen
                            ? 'bg-neutral-200 text-[rgb(var(--brand-black-rgb)_/_0.9)]'
                            : 'bg-neutral-100 hover:bg-neutral-200 text-[rgb(var(--brand-black-rgb)_/_0.7)]'
                        }`}
                      >
                        <Plus className="w-4 h-4 shrink-0" strokeWidth={2.25} />
                        {esV2 && (
                          <span className="hidden md:inline text-[13.5px] font-medium whitespace-nowrap">
                            Agregar
                          </span>
                        )}
                      </button>
                      {/* El popup de desktop NO cuelga de este botón: en v2 vive
                          al pie del `inputBox` y ocupa el ancho de la caja
                          entera. En v1 sigue acá, angosto y anclado al "+". */}
                      {attachOpen && !esV2 && (
                        <div className="hidden md:block absolute bottom-full left-0 mb-2 w-[280px] bg-white border border-black/[0.06] rounded-2xl shadow-xl p-2 z-[56]">
                          {attachMenuContent}
                        </div>
                      )}
                    </div>
                  )}
                  {/* Desplegable "Ayuda de IA" (switch + Inteligencia). En v2 no
                      existe: la ayuda va prendida por detrás y no hay nada que
                      decidir acá. El `md:ml-auto` que empuja el resto de la fila a la
                      derecha vivía en ESTE div, así que sin el menú lo hereda un
                      spacer vacío — sacarlo del todo pegaba los créditos y la flecha
                      al centro de la fila. */}
                  {esV2 ? (
                    <div className="md:ml-auto" />
                  ) : (
                    <div className="md:ml-auto">
                      <AyudaIAMenu
                        aiHelp={aiHelp}
                        onToggleAiHelp={handleToggleAiHelp}
                        intelligence={intelligence}
                        onChangeIntelligence={setIntelligence}
                        open={aiMenuOpen}
                        onOpenChange={setAiMenuOpen}
                      />
                    </div>
                  )}
                  {chat.streaming ? (
                    // Turno en curso → botón Detener (escape hatch si el
                    // stream se colgó; también corta un turno largo a
                    // propósito).
                    <button
                      type="button"
                      onClick={chat.stopTurn}
                      aria-label="Detener"
                      // Fondo SÓLIDO (no `bg-brand-black/85`): brand-black es
                      // un `var(--brand-black)` con hex → el modificador de
                      // opacidad genera CSS inválido y el fondo NO se aplica
                      // (el botón quedaba invisible salvo en hover). Ver ADR-140.
                      className="relative shrink-0 ml-auto md:ml-0 inline-flex items-center justify-center w-9 h-9 rounded-full shadow-sm bg-brand-black hover:opacity-90 text-white transition-opacity duration-200"
                    >
                      <Square className="w-3 h-3 fill-current" />
                    </button>
                  ) : (
                    <>
                      {/* Costo en créditos — SOLO en modo directo, donde el
                          precio es determinista (sabemos exactamente qué se
                          va a generar). Con Ayuda de IA el agente puede hacer
                          cualquier cosa, así que anunciar un número sería
                          mentir. Aparece recién cuando hay texto: sin prompt
                          no hay nada que cobrar. */}
                      {/* Pill de créditos y flecha de enviar: en MOBILE solo
                          existen cuando el agente ya está conversando. En el
                          resto de los casos hay un botón "Generar" de ancho
                          completo debajo de la caja, que ya lleva el costo
                          adentro — tener las dos cosas duplicaba la acción y el
                          número. En desktop van siempre (no hay botón grande). */}
                      {!aiHelp && hasText && (
                        <span
                          className={`shrink-0 ml-auto md:ml-0 items-center h-7 px-2.5 rounded-full bg-neutral-100 text-[12.5px] font-medium text-[rgb(var(--brand-black-rgb)_/_0.7)] whitespace-nowrap tabular-nums ${
                            agenteConversando ? 'inline-flex' : 'hidden md:inline-flex'
                          }`}
                        >
                          {directCreditCost}{' '}
                          {directCreditCost === 1 ? 'crédito' : 'créditos'}
                        </span>
                      )}
                      {/* MIC del dictado por voz. Antes se TURNABA con la flecha
                          de enviar (mic con la caja vacía, flecha con texto);
                          ahora el lugar de la flecha lo ocupa "Crear", que está
                          siempre, así que el mic pasó a convivir con él. Sigue
                          apareciendo sólo con la caja vacía —dictar sobre un
                          texto ya escrito no es lo que hace— y sólo en
                          navegadores con Web Speech API (Firefox no). */}
                      {mostrarMic && (
                        <button
                          type="button"
                          onClick={() => setDictando(true)}
                          aria-label="Dictar por voz"
                          // El `ml-auto` que empuja el grupo a la derecha lo lleva
                          // SOLO el primero de [pill de créditos] [mic] [Crear]:
                          // con dos, el segundo empuja de nuevo y se abre un hueco
                          // entre ellos. Antes no podía pasar —el pill sale con
                          // texto y el mic salía sin texto, o sea nunca juntos—,
                          // pero desde que el mic no desaparece al escribir sí.
                          className={`relative shrink-0 ${
                            !aiHelp && hasText ? '' : 'ml-auto md:ml-0'
                          } inline-flex items-center justify-center w-9 h-9 rounded-full bg-neutral-100 hover:bg-neutral-200 text-[rgb(var(--brand-black-rgb)_/_0.7)] transition-colors`}
                        >
                          <Mic className="w-4 h-4" strokeWidth={2.25} />
                        </button>
                      )}
                      {/* "Crear" — reemplaza a la flecha de enviar. Está SIEMPRE
                          (la flecha sólo tomaba color con texto), y sin texto se
                          ve atenuado y no se puede accionar: es la promesa de la
                          pantalla a la vista desde el principio, no un botón que
                          aparece de la nada al escribir la primera letra.

                          El gradiente es el MISMO del "Generar imagen" de la
                          pantalla de generar (`AccountSetupProduct`). Allá el
                          deshabilitado es un gris sólido y el gradiente entra
                          por crossfade de dos capas; acá se pidió el propio
                          gradiente atenuado, así que es UNA capa con `opacity` —
                          más simple, y encima transiciona (de color sólido a
                          gradiente no se puede animar, que es justamente por qué
                          allá hay dos capas).

                          Conserva la regla de visibilidad de la flecha: en
                          mobile sólo con el agente ya conversando. Sino duplica
                          el "Generar" de ancho completo que vive debajo de la
                          caja, con su costo adentro. */}
                      <button
                        type="button"
                        onClick={puedeCrear ? () => handleGenerate() : undefined}
                        disabled={!puedeCrear}
                        className={`relative shrink-0 ${
                          mostrarMic || (!aiHelp && hasText) ? '' : 'ml-auto md:ml-0'
                        } items-center justify-center gap-1.5 h-9 px-4 rounded-full bg-[linear-gradient(181.83deg,#2563F5_8.76%,#0436AE_90.56%)] text-[13.5px] font-semibold text-white transition-opacity duration-200 ${
                          agenteConversando ? 'inline-flex' : 'hidden md:inline-flex'
                        } ${
                          puedeCrear
                            ? 'opacity-100 hover:brightness-110'
                            : 'opacity-40 cursor-not-allowed'
                        }`}
                      >
                        <Sparkles className="w-4 h-4" strokeWidth={2.25} />
                        Crear
                      </button>
                    </>
                  )}
                </div>
                </>
                )}
                {/* Menú "Agregar" de DESKTOP en v2 — cuelga de la CAJA, no del
                    botón: ocupa su ancho entero (`left-0 right-0`) y se lee como
                    una extensión del composer, no como un popover flotante. Por
                    eso el `relative` del contenedor de arriba.

                    Se despliega hacia ABAJO en el empty state, que es donde se
                    pidió y donde hay lugar. Con la conversación andando el
                    composer está anclado al pie del sheet, que además es
                    `md:overflow-hidden`: hacia abajo el menú caería fuera y se
                    recortaría entero, así que ahí sigue abriendo hacia arriba.

                    Lleva `data-attach-menu` propio: el handler de click-afuera
                    busca ese marcador subiendo por el árbol, y acá el menú ya no
                    está adentro del wrapper del botón que lo tenía.

                    En v1 no pasa por acá: allá el popup sigue anclado al "+",
                    angosto, porque su fila de controles es otra. */}
                {esV2 && attachOpen && (
                  <div
                    data-attach-menu
                    className={`hidden md:block absolute left-0 right-0 bg-white border border-black/[0.06] rounded-2xl shadow-xl p-2 z-[56] ${
                      hasMessages ? 'bottom-full mb-2' : 'top-full mt-2'
                    }`}
                  >
                    {attachMenuContent}
                  </div>
                )}
              </div>
            );

            // Entrada a referencias del empty state — SOLO mobile. Las tres
            // cards de escritorio se quitaron
            // (ADR-2026-09-08-asistente-v2-sin-aura-ni-cards); en desktop este
            // bloque no renderiza nada.
            const accionCards = (
              <AccionCardsRow
                onOpenReferencias={() => setReferenciasOpen(true)}
                referencias={chat.attachments.map((a) => ({
                  id: a.id,
                  url:
                    miniaturaDeAdjunto(a),
                  label: a.kind === 'image' ? 'Imagen' : a.name,
                }))}
                onRemoveReferencia={chat.removeAttachment}
              />
            );

            // Botón Generar de ancho completo — SOLO mobile, y solo cuando la
            // flecha del composer NO está (o sea, salvo con el agente ya
            // conversando). Es el mismo botón que cierra los demás modos
            // (ConfigurePanel), así que llegar acá desde otro modo no cambia
            // de lenguaje. En desktop nunca aparece: ahí manda la flecha.
            const generarButton = agenteConversando ? null : (
              <button
                type="button"
                onClick={hasText && !sendDisabled ? handleGenerate : undefined}
                disabled={sendDisabled || !hasText}
                className="md:hidden w-full max-w-[680px] inline-flex items-center justify-center gap-2 py-3.5 px-5 rounded-2xl bg-brand-primary hover:bg-brand-primary-hover text-white text-[15.5px] font-semibold shadow-sm transition-colors disabled:opacity-40 disabled:cursor-not-allowed"
              >
                <Sparkles className="w-5 h-5" />
                <span>Generar</span>
                {/* Costo solo en modo directo: con Ayuda de IA el agente puede
                    hacer cualquier cosa y anunciar un número sería mentir. */}
                {!aiHelp && (
                  <span className="inline-flex items-center gap-1 bg-white text-brand-black px-2 py-0.5 rounded-full text-[12.5px] font-semibold tabular-nums">
                    <img
                      src="/images/token_icon.png"
                      alt=""
                      aria-hidden="true"
                      className="w-3.5 h-3.5 object-contain"
                    />
                    {directCreditCost}
                  </span>
                )}
              </button>
            );

            // Las propuestas de generación ya se renderizan inline en el
            // stream (MessageRow con showPendingCards), así que el slot solo
            // se queda con los pickers — que dependen de su scroll y su
            // sticky para funcionar.
            const slotCard =
              chat.activeToolCard &&
              !isInlinePendingCard(chat.activeToolCard) &&
              // La encuesta con "Prefiero escribirlo" deja la caja (ADR-2026-10-22-la-encuesta-reemplaza-la-caja-de-texto).
              !(chat.activeToolCard.kind === 'preguntar' && chat.activeToolCard.id === escribirEnLugarDeEncuesta)
                ? chat.activeToolCard
                : null;
            const toolCardSlot = slotCard && (
              <div
                key={slotCard.id}
                className={`w-full ${anchoComposer} animate-mode-swap overflow-y-auto rounded-2xl bg-white shadow-[0_2px_14px_-6px_rgba(0,0,0,0.12),0_1px_3px_rgba(0,0,0,0.04)] ring-1 ring-black/[0.04]`}
                style={{ maxHeight: '65dvh' }}
              >
                <ToolCardRenderer
                  card={slotCard}
                  chat={chat}
                  embedded
                  onPreferirEscribir={
                    slotCard.kind === 'preguntar' ? () => setEscribirEnLugarDeEncuesta(slotCard.id) : undefined
                  }
                />
              </div>
            );

            if (!hasMessages) {
              // ── Empty state ──────────────────────────────────────────
              // Desktop: bloque centrado (título + input + atajos) + el
              // "continuar chat" va al footer de abajo.
              // Mobile: el título se centra arriba y el cluster queda
              // anclado al pie en orden atajos → continuar → input (input
              // abajo del todo). Logrado con `order-*` por breakpoint sin
              // duplicar el input (su ref es única).
              return (
                <div
                  className="relative z-10 flex-1 min-h-0 overflow-y-auto flex flex-col px-5 md:px-8 md:items-center md:justify-center md:py-6"
                  style={{
                    paddingBottom:
                      'calc(env(safe-area-inset-bottom, 0px) + 1.5rem)',
                  }}
                >
                  {/* Toggle Imagen|Video — MOBILE, arriba del título. En
                      desktop vive pegado al input (ver más abajo), donde el
                      bloque entero está centrado verticalmente. */}
                  {!esV2 && (
                    <div className="order-0 md:hidden w-full flex justify-center pt-3 pb-6">
                      <OutputKindToggle
                        value={outputKind}
                        onChange={cambiarOutputKind}
                      />
                    </div>
                  )}
                  {/* Título — mobile: ocupa el espacio de arriba y se centra;
                      desktop: parte del bloque centrado. Al enfocar el input
                      en mobile (teclado abierto) se desvanece sutilmente
                      (en desktop siempre visible). */}
                  <div
                    // En desktop el título respira más: el bloque de abajo
                    // arranca con el toggle Imagen/Video, y con mb-10 le
                    // quedaba encima. En mobile no aplica (el título ocupa el
                    // espacio de arriba con flex-1 y el toggle vive aparte).
                    className={`order-1 flex-1 md:flex-none flex items-center justify-center transition-opacity duration-300 md:opacity-100 ${
                      // v2 apila mascota sobre título, así que la caja es columna. En
                      // v1 sigue siendo una fila con el <h1> solo adentro.
                      esV2 ? 'flex-col gap-4 md:gap-5' : ''
                    } ${
                      // Los 80px de v1 existían porque abajo arrancaba el toggle
                      // Imagen/Video, que en v2 no está. v2 arrancó en 32px (título
                      // pegado al composer, como en la referencia —
                      // ADR-2026-09-08-el-composer-de-v2-se-abre-al-escribir) y hoy
                      // va en 64: con el verbo tipeado, el título pide aire propio.
                      esV2 ? 'md:mb-16' : 'md:mb-20'
                    } ${inputFocused ? 'opacity-0' : 'opacity-100'}`}
                  >
                    {/* Cumbre, la mascota del chat — ADR-2026-09-08-cumbre-en-el-empty-state-de-v2.
                        En reposo (`idle`): flota y parpadea cada tanto. Los otros dos
                        estados (`thinking` / `answering`) existen en el componente pero
                        acá no se usan: esto es el empty state, donde por definición no
                        hay turno en curso.

                        `decorativa`: al lado está el <h1>, que ya nombra la pantalla.
                        Anunciarla además como "Asistente Atlas" haría que el lector de
                        pantalla lea dos veces lo mismo.

                        El tamaño va por CLASE y no por `size` porque cambia con el
                        breakpoint; el `size` queda de respaldo si el CSS no cargó. */}
                    {esV2 && (
                      <CumbreMascot
                        state="idle"
                        size={120}
                        decorativa
                        className="w-[92px] md:w-[120px] h-auto shrink-0"
                      />
                    )}
                    {/* Siempre brand-black: sin aura detrás, el fondo del sheet
                        es claro en los dos modos y el blanco quedaría
                        invisible. Ya no depende de `aiHelp`. */}
                    {/* "Crea" en la fuente de la app (font-sans, Plus Jakarta) y
                        "sin límites" en la display serif en itálica. El contraste
                        entre las dos es intencional — antes iba todo en Playfair.
                        En v1 van apiladas (`block`); en v2, en UNA sola línea. */}
                    <h1
                      className={`text-[32px] md:text-[44px] font-normal text-center leading-tight ${
                        // v2: el título va en gradiente azul (`bg-clip-text` +
                        // texto transparente). v1 lo mantiene en brand-black.
                        esV2
                          ? 'bg-clip-text text-transparent'
                          : 'text-brand-black'
                      }`}
                      style={
                        esV2
                          ? {
                              // Diagonal suave para que el degradado se note en una
                              // sola línea: de arriba-izquierda (azul profundo) a
                              // abajo-derecha (azul claro). El punto medio en el azul
                              // de marca es lo que lo ata al resto de la pantalla.
                              backgroundImage:
                                'linear-gradient(105deg, color-mix(in srgb, var(--brand-primary) 44%, black) 0%, var(--brand-primary) 52%, color-mix(in srgb, var(--brand-primary) 69%, white) 100%)',
                            }
                          : undefined
                      }
                      // El verbo se tipea y cambia solo: para el lector de
                      // pantalla el título es una frase fija, no un deletreo.
                      aria-label={`${VERBOS[0]} sin límites`}
                    >
                      <VerboTipeado
                        className={`font-sans font-semibold ${esV2 ? '' : 'block'}`}
                      />
                      {/* El espacio va explícito: entre dos `{...}` JSX se come el
                          salto de línea del código y las dos palabras quedarían
                          pegadas ("Creásin límites"). */}
                      {esV2 && ' '}
                      <span
                        className={`font-display italic ${esV2 ? '' : 'block'}`}
                      >
                        sin límites
                      </span>
                    </h1>
                  </div>
                  {/* Sugerencias — DEBAJO del input en los dos breakpoints
                      (`order-5 md:order-3`, contra el input en `order-4
                      md:order-2`). Estuvieron arriba, entre el título y el
                      input; se bajaron para probar cómo lee
                      (ADR-2026-09-08-las-sugerencias-se-prueban-debajo-del-input,
                      que supersede a ADR-2026-09-08-sugerencias-que-arrancan-el-turno
                      en el punto del orden).

                      Un click MANDA el prompt y arranca el turno; no pre-llena
                      el textarea. Sólo con Ayuda de IA — ver `SUGERENCIAS`.

                      Mobile: fila con scroll horizontal full-bleed (`-mx-5`),
                      que necesita un padre BLOCK — dentro de un `flex
                      justify-center` el margen negativo se compensa solo y no
                      llega a los bordes. Por eso el centrado es `md:flex`.
                      Desktop: wrap centrado, que con cuatro pills entra en una
                      línea y no obliga a descubrir nada scrolleando. */}
                  {aiHelp && (
                    <div className="order-5 md:order-3 w-full max-w-3xl mx-auto mt-4 md:mt-6">
                      <div
                        className="-mx-5 px-5 overflow-x-auto scrollbar-hide md:mx-0 md:px-0 md:overflow-visible"
                        style={{
                          scrollbarWidth: 'none',
                          msOverflowStyle: 'none',
                          WebkitOverflowScrolling: 'touch',
                        }}
                      >
                        <div className="flex md:flex-wrap md:justify-center items-center gap-2">
                          {SUGERENCIAS.map(({ key, label, prompt: texto, Icon }) => (
                            <button
                              key={key}
                              type="button"
                              onClick={() => handleGenerate(texto)}
                              disabled={sendDisabled}
                              // Azul clarito con el texto y el ícono en
                              // `brand-primary`. El fondo va en hex literal y NO
                              // como `bg-brand-primary/10`: los tokens `brand-*`
                              // son `var(--brand-primary)` pelado, sin
                              // `<alpha-value>`, así que el modificador de
                              // opacidad no emite una regla válida. Los dos
                              // valores son el azul de marca (var(--brand-primary)) tintado
                              // sobre blanco al ~8% y ~15%.
                              className="shrink-0 inline-flex items-center gap-2 py-2 px-4 rounded-full bg-[rgb(var(--brand-primary-rgb)_/_0.095)] hover:bg-[rgb(var(--brand-primary-rgb)_/_0.165)] text-[13.5px] font-semibold text-brand-primary transition-colors disabled:opacity-40 disabled:cursor-not-allowed"
                            >
                              <Icon className="w-4 h-4 shrink-0" strokeWidth={2.25} />
                              <span className="whitespace-nowrap">{label}</span>
                            </button>
                          ))}
                        </div>
                      </div>
                    </div>
                  )}
                  {/* Input — mobile order-4 (con las sugerencias debajo, en 5);
                      desktop bajo el título (order-2). */}
                  <div className="order-4 md:order-2 w-full max-w-3xl mx-auto flex flex-col items-center mt-5 md:mt-0">
                    {/* Toggle Imagen|Video — SOLO desktop: en mobile vive
                        arriba de todo, en el chrome del Sidebar (y se repite
                        dentro del menú de la ruedita como red de seguridad).
                        Va arriba del aviso de créditos, que está diseñado
                        como extensión pegada al input (`-mb-3`) y no admite
                        nada entre medio. */}
                    {!esV2 && (
                      <div className="hidden md:flex mb-3 justify-center">
                        <OutputKindToggle
                          value={outputKind}
                          onChange={cambiarOutputKind}
                        />
                      </div>
                    )}
                    <div className="relative z-10 w-full flex justify-center">
                      {inputBox}
                    </div>
                    {/* Generar full-width — mobile, debajo del container del
                        prompt, como en los demás modos. */}
                    <div className="w-full flex justify-center mt-3">
                      {generarButton}
                    </div>
                  </div>
                  {/* Cards de acción — visibles SIEMPRE en el empty state, en
                      ambos breakpoints. A diferencia de las viejas pills (que
                      en mobile aparecían recién al enfocar el input), las
                      cards son la entrada principal al modo: tienen que verse
                      al llegar. Mobile: arriba del input (order-3, el input es
                      order-4). Desktop: debajo (md:order-3, input md:order-2). */}
                  {/* Block en mobile, flex centrado en desktop. El carrusel
                      de referencias necesita un padre BLOCK para poder salirse
                      a los bordes con `-mx-5`: dentro de un `flex
                      justify-center` el margen negativo se compensa solo
                      (achica la caja de margen, el centrado reparte esos 40px
                      y el borde queda donde estaba). */}
                  {/* `md:hidden`: en desktop AccionCardsRow ya no renderiza
                      nada, pero el wrapper seguiría reservando su margen. */}
                  <div className="order-3 md:hidden w-full max-w-3xl mx-auto mt-4">
                    {accionCards}
                  </div>
                </div>
              );
            }

            // ── Active state ───────────────────────────────────────────
            // Mensajes scrolleables arriba, input (o tool card) anclado
            // al pie con safe-area-inset.
            return (
              <>
                <div
                  ref={messagesScrollRef}
                  onScroll={handleMessagesScroll}
                  onWheel={releasePinOnUserScroll}
                  onTouchMove={releasePinOnUserScroll}
                  className="relative z-10 flex-1 min-h-0 overflow-y-auto px-5 md:px-8"
                  style={{ scrollbarWidth: 'thin' }}
                >
                  <div
                    ref={messagesContentRef}
                    className="max-w-3xl mx-auto flex flex-col gap-4 pt-6 pb-28"
                  >
                    {/* Las rondas que sólo hicieron acciones, como líneas de lo que hizo; un mensaje
                        vacío no se dibuja (ADR-2026-10-22-cada-accion-del-asistente-queda-en-una-linea). */}
                    {filasDeLaConversacion(chat.messages).map((f) =>
                      f.tipo === 'acciones' ? (
                        <div key={f.clave} className="-mt-2 px-0.5">
                          <LineasDeAcciones lineas={f.lineas} />
                        </div>
                      ) : (
                        <MessageRow
                          key={f.indice}
                          msg={chat.messages[f.indice]!}
                          chat={chat}
                          showPendingCards
                          userRef={f.indice === lastUserIdx ? latestUserMsgRef : undefined}
                        />
                      ),
                    )}
                    {(() => {
                      // Indicador de actividad. Si hay una tool server-side en
                      // uso (web search / scraper), mostramos su label real.
                      // Sino, "Pensando…" mientras el agente prepara la
                      // respuesta/card y no hay nada visible todavía.
                      if (chat.streaming && chat.toolActivity) {
                        return (
                          <ThinkingIndicator
                            label={chat.toolActivity}
                            tipo={tipoDeActividad(chat.toolActivity)}
                            mascota={esV2}
                          />
                        );
                      }
                      const last = chat.messages[chat.messages.length - 1];
                      const preparing =
                        chat.streaming &&
                        !chat.activeToolCard &&
                        (!last ||
                          last.role === 'user' ||
                          (last.role === 'assistant' &&
                            !last.text &&
                            (!last.toolCards || last.toolCards.length === 0)));
                      return preparing ? <ThinkingIndicator mascota={esV2} /> : null;
                    })()}
                  </div>
                </div>
                <div
                  className="relative z-10 shrink-0 px-5 md:px-8 pt-3"
                  style={{
                    paddingBottom:
                      'calc(env(safe-area-inset-bottom, 0px) + 1.5rem)',
                  }}
                >
                  <div className="max-w-3xl mx-auto flex flex-col items-center">
                    {/* El toggle viaja con el composer: en el estado activo
                        también tiene que estar, porque decide qué genera el
                        PRÓXIMO prompt. Acá va en AMBOS breakpoints — en el
                        empty state mobile vive arriba del título, pero en el
                        activo no hay título, así que este es su lugar. Se
                        oculta mientras una tool card ocupa el slot: ahí no
                        hay caja de prompt a la que aplicarle el toggle. Y
                        tampoco va si el agente ya está conversando: ahí el
                        tipo de salida lo decide él. */}
                    {!esV2 && !toolCardSlot && !agenteConversando && (
                      <div className="flex mb-3 justify-center">
                        <OutputKindToggle
                          value={outputKind}
                          onChange={cambiarOutputKind}
                        />
                      </div>
                    )}
                    {toolCardSlot ?? inputBox}
                    {/* Generar full-width también en el estado activo: en modo
                        directo la flecha del composer no existe en mobile, así
                        que sin esto no habría con qué enviar el segundo
                        prompt. No va si una tool card ocupa el slot. */}
                    {!toolCardSlot && generarButton && (
                      <div className="w-full flex justify-center mt-3">
                        {generarButton}
                      </div>
                    )}
                  </div>
                </div>
              </>
            );
          })()}

          {/* Aviso de consumo — al pie del sheet, centrado y sin caja: es una
              nota al pie, no un control. Ya no se puede descartar (antes era
              una franja gris con una x arriba del input, persistida en
              localStorage) — ver ADR-2026-09-08-el-aviso-de-consumo-es-una-nota-al-pie.
              SOLO con Ayuda de IA: en modo directo no hay costo de
              razonamiento y el texto sería falso. Sólo en el empty state; con
              el chat andando el pie lo ocupa el input.

              Acá vivía el botón "Continuar con el chat anterior", que se quitó
              en ADR-2026-09-08-asistente-v2-sin-aura-ni-cards. */}
          {!hasMessages && aiHelp && (
            <div
              className="relative z-10 shrink-0 px-5 md:px-8 pt-1"
              style={{
                paddingBottom: 'calc(env(safe-area-inset-bottom, 0px) + 1rem)',
              }}
            >
              <p className="text-center text-[12.5px] font-medium text-neutral-500 leading-snug">
                El asistente consumirá créditos basados en su uso
              </p>
            </div>
          )}
        </div>

        {/* ── Opciones del chat (Consumo / Memoria / Historial) — MOBILE ── */}
        {/* En desktop esto es el popover colgado del botón "…" del encabezado;
            acá abajo es un sheet, que es el patrón que ya usan el historial y la
            memoria en mobile. Portaleado por lo mismo que ellos: el sheet del
            asistente tiene `transform`, así que un `fixed` anidado se ancla a ESE
            contenedor y no cubre la sidebar
            (ADR-2026-09-11-consumo-y-memoria-vuelven-a-tener-entrada). */}
        {esV2 &&
          menuOpen &&
          ReactDOM.createPortal(
            <>
              <div
                className="md:hidden fixed inset-0 z-[63] bg-black/40 animate-fade-in"
                onClick={() => setMenuOpen(false)}
              />
              <div
                className="md:hidden fixed inset-x-0 bottom-0 z-[64] bg-white rounded-t-[20px] shadow-2xl flex flex-col animate-slide-up-sheet"
                style={{
                  maxHeight: '80dvh',
                  paddingBottom:
                    'calc(env(safe-area-inset-bottom, 0px) + 12px)',
                }}
              >
                <div className="py-2 shrink-0">
                  <div className="w-9 h-[5px] bg-black/[0.15] rounded-full mx-auto" />
                </div>
                <div className="flex-1 overflow-y-auto px-3 pb-2">
                  {menuContent}
                </div>
              </div>
            </>,
            document.body,
          )}

        {/* ── Historial de chats (slide-in) ── */}
        {/* Portaleado a document.body: el sheet del asistente tiene un
            `transform` (animación) → un `fixed inset-0` anidado se ancla a ESE
            contenedor (offset por la sidebar) y no cubre la sidebar. El portal
            lo devuelve al viewport real. z-[63] > z-30 de la sidebar. */}
        {historialOpen &&
          ReactDOM.createPortal(
            <>
            <div
              className="fixed inset-0 z-[63] bg-black/40 animate-fade-in"
              onClick={() => setHistorialOpen(false)}
            />
            {/* Mobile: bottom sheet */}
            <div
              className="md:hidden fixed inset-x-0 bottom-0 z-[64] bg-white rounded-t-[20px] shadow-2xl flex flex-col animate-slide-up-sheet"
              style={{
                maxHeight: '80dvh',
                paddingBottom: 'calc(env(safe-area-inset-bottom, 0px) + 12px)',
              }}
            >
              <div className="py-2 shrink-0">
                <div className="w-9 h-[5px] bg-black/[0.15] rounded-full mx-auto" />
              </div>
              <div className="flex items-center justify-between px-4 pb-2 shrink-0">
                <h3 className="text-[15px] font-semibold text-brand-black/90">
                  Historial de chats
                </h3>
                <div className="flex items-center gap-1">
                  {historialBorrarTodo}
                  <button
                    type="button"
                    onClick={() => setHistorialOpen(false)}
                    className="w-8 h-8 inline-flex items-center justify-center rounded-full bg-black/[0.06] text-brand-black/40 hover:bg-black/[0.1] transition-colors"
                    aria-label="Cerrar"
                  >
                    <X className="w-4 h-4" />
                  </button>
                </div>
              </div>
              <div className="flex-1 overflow-y-auto px-3 pb-4">
                {historialListContent}
              </div>
            </div>
            {/* Desktop: side panel */}
            <div className="hidden md:flex fixed top-4 right-4 bottom-4 w-[420px] z-[64] bg-white rounded-[13px] shadow-2xl flex-col overflow-hidden">
              <div className="flex items-center justify-between px-5 py-4 shrink-0">
                <h3 className="text-[17px] font-semibold text-brand-black/90">
                  Historial de chats
                </h3>
                <div className="flex items-center gap-1">
                  {historialBorrarTodo}
                  <button
                    type="button"
                    onClick={() => setHistorialOpen(false)}
                    className="w-8 h-8 inline-flex items-center justify-center rounded-full bg-black/[0.06] text-brand-black/40 hover:bg-black/[0.1] transition-colors"
                    aria-label="Cerrar"
                  >
                    <X className="w-4 h-4" />
                  </button>
                </div>
              </div>
              <div className="mx-5 h-px bg-black/[0.08] shrink-0" />
              <div className="flex-1 overflow-y-auto px-3 py-3">
                {historialListContent}
              </div>
            </div>
            </>,
            document.body,
          )}

        {/* ── Memoria del asistente (slide-in, mismo patrón que historial) ── */}
        {memoriaOpen &&
          ReactDOM.createPortal(
            <>
            <div
              className="fixed inset-0 z-[63] bg-black/40 animate-fade-in"
              onClick={() => setMemoriaOpen(false)}
            />
            {/* Mobile: bottom sheet */}
            <div
              className="md:hidden fixed inset-x-0 bottom-0 z-[64] bg-white rounded-t-[20px] shadow-2xl flex flex-col animate-slide-up-sheet"
              style={{
                maxHeight: '80dvh',
                paddingBottom: 'calc(env(safe-area-inset-bottom, 0px) + 12px)',
              }}
            >
              <div className="py-2 shrink-0">
                <div className="w-9 h-[5px] bg-black/[0.15] rounded-full mx-auto" />
              </div>
              <div className="px-4 pb-1 shrink-0">
                <div className="flex items-center justify-between">
                  <h3 className="text-[15px] font-semibold text-brand-black/90">
                    Memoria del asistente
                  </h3>
                  <button
                    type="button"
                    onClick={() => setMemoriaOpen(false)}
                    className="w-8 h-8 inline-flex items-center justify-center rounded-full bg-black/[0.06] text-brand-black/40 hover:bg-black/[0.1] transition-colors"
                    aria-label="Cerrar"
                  >
                    <X className="w-4 h-4" />
                  </button>
                </div>
                <p className="text-[12.5px] text-[#3C3C43]/55 leading-snug pb-1">
                  Lo que el asistente recuerda de vos y tu marca entre chats.
                </p>
              </div>
              <div className="flex-1 overflow-y-auto px-3 pb-4">
                {memoriaListContent}
              </div>
            </div>
            {/* Desktop: side panel */}
            <div className="hidden md:flex fixed top-4 right-4 bottom-4 w-[420px] z-[64] bg-white rounded-[13px] shadow-2xl flex-col overflow-hidden">
              <div className="px-5 pt-4 pb-3 shrink-0">
                <div className="flex items-center justify-between">
                  <h3 className="text-[17px] font-semibold text-brand-black/90">
                    Memoria del asistente
                  </h3>
                  <button
                    type="button"
                    onClick={() => setMemoriaOpen(false)}
                    className="w-8 h-8 inline-flex items-center justify-center rounded-full bg-black/[0.06] text-brand-black/40 hover:bg-black/[0.1] transition-colors"
                    aria-label="Cerrar"
                  >
                    <X className="w-4 h-4" />
                  </button>
                </div>
                <p className="text-[13px] text-[#3C3C43]/55 leading-snug mt-1">
                  Lo que el asistente recuerda de vos y tu marca entre chats.
                </p>
              </div>
              <div className="mx-5 h-px bg-black/[0.08] shrink-0" />
              <div className="flex-1 overflow-y-auto px-3 py-3">
                {memoriaListContent}
              </div>
            </div>
            </>,
            document.body,
          )}


        {/* Mobile bottom sheet del menú "+". Solo se monta cuando
            attachOpen=true y estamos en mobile. */}
        {attachOpen && (
          <div className="md:hidden fixed inset-0 z-[62]">
            <div
              className="absolute inset-0 bg-black/40 animate-fade-in"
              onClick={() => setAttachOpen(false)}
            />
            <div
              data-attach-menu
              className="absolute inset-x-0 bottom-0 bg-white rounded-t-[20px] shadow-2xl p-3 animate-slide-up-sheet"
              style={{
                paddingBottom: 'calc(env(safe-area-inset-bottom, 0px) + 12px)',
              }}
            >
              <div className="w-9 h-[5px] bg-black/[0.15] rounded-full mx-auto mb-3" />
              {attachMenuContent}
            </div>
          </div>
        )}
      </div>

      {/* Input de archivo oculto — "Añadir imagen" del menú "+". */}
      <input
        ref={fileInputRef}
        type="file"
        accept="image/*"
        className="hidden"
        onChange={(e) => {
          const f = e.target.files?.[0] ?? null;
          onPickImageFile(f);
          // Permitir re-elegir el mismo archivo.
          e.target.value = '';
        }}
      />

      {/* Picker real de productos (mismo que el agente / modo modelo). */}
      {attachPicker === 'product' && (
        <ProductFullMenu
          initialSelected={[]}
          allowMulti
          onCancel={() => setAttachPicker('none')}
          onAccept={onAcceptProducts}
        />
      )}

      {/* Picker de poses. `selectedIds` va SIEMPRE vacío y no refleja lo ya
          adjuntado: acá la pose no es un estado que se edita (como en el modo
          Modelo, donde se eligen las poses de la tanda) sino un adjunto que se
          agrega. Arrancar con las anteriores marcadas invitaría a destildarlas,
          y destildar no saca el chip — el chip se saca desde el chip. */}
      <PosePickerSheet
        open={attachPicker === 'pose'}
        poses={poses}
        selectedIds={[]}
        onChange={onAcceptPoses}
        onClose={() => setAttachPicker('none')}
      />

      {/* Picker de imágenes ya generadas. */}
      <GeneratedImagesPicker
        open={attachPicker === 'generated'}
        companyId={companyId}
        onCancel={() => setAttachPicker('none')}
        onAccept={onAcceptGenerated}
      />

      {/* Picker real de modelos. */}
      <ModelPickerSheet
        open={attachPicker === 'model'}
        selection={null}
        onChange={onPickModel}
        onClose={() => setAttachPicker('none')}
        userModels={userModels}
        userId={userId}
        companyId={companyId}
        onModelCreated={refreshModels}
        hideAuto
      />

      {/* Pantalla de carga tradicional — SOLO modo directo. Mismo componente
          que usan los demás modos (imagen: 80s como en toda la app; video:
          barra larga porque la generación tarda varios minutos). Al
          completarse salta a la pantalla de generadas. */}
      {showLoadingScreen && (
        <LoadingAnimation
          message={
            loadingKind === 'video'
              ? 'Generando video...'
              : 'Generando imágenes...'
          }
          subMessage={
            loadingKind === 'video'
              ? 'Esto puede tardar varios minutos'
              : 'Esto puede tardar unos segundos'
          }
          durationMs={loadingKind === 'video' ? 600_000 : 80_000}
          completeNow={completeProgressNow}
          onCompleted={() => {
            setShowLoadingScreen(false);
            setCompleteProgressNow(false);
            const pend = pendingNavRef.current;
            pendingNavRef.current = null;
            if (pend) {
              navigate('/redes-sociales/imagenes-generadas', {
                state: { imageIds: pend.imageIds, companyId: pend.companyId },
              });
            }
          }}
        />
      )}

      {/* Sheet único de referencias — MOBILE. Reemplaza a las 3 cards
          sueltas: una sola puerta con pestañas Subidas/Productos/Modelos. */}
      <ReferenciasSheet
        open={referenciasOpen}
        onClose={() => setReferenciasOpen(false)}
        attachments={chat.attachments}
        onRemoveAttachment={chat.removeAttachment}
        models={userModels}
        onUploadImage={() => fileInputRef.current?.click()}
        // Commit único de "Utilizar". La selección de productos que llega es
        // la COMPLETA, así que sincronizamos en los dos sentidos: sacamos los
        // chips que el user deseleccionó y agregamos los nuevos. Sin esto,
        // deseleccionar en el panel no quitaba el chip.
        onUtilizar={({ products, model }) => {
          const nuevos = new Set(products.map((p) => p.id));
          for (const att of chat.attachments) {
            if (att.kind === 'product' && !nuevos.has(att.productId)) {
              chat.removeAttachment(att.id);
            }
          }
          const yaAdjuntos = new Set(
            chat.attachments
              .filter(
                (a): a is Extract<ChatAttachment, { kind: 'product' }> =>
                  a.kind === 'product',
              )
              .map((a) => a.productId),
          );
          onAcceptProducts(
            products.map((p) => p.id).filter((id) => !yaAdjuntos.has(id)),
          );
          // `null` = no se tocó el modelo, así que no lo pisamos.
          if (model) onPickModel(model);
        }}
        uploading={imageUploading}
      />

      {/* Lightbox de adjunto — imagen ampliada con fondo desenfocado. Si es
          producto o modelo, el nombre aparece sutil debajo. */}
      {previewAttachment && (
        <div
          className="fixed inset-0 z-[300] flex flex-col items-center justify-center px-6 bg-black/55 backdrop-blur-md animate-fade-in"
          onClick={() => setPreviewAttachment(null)}
        >
          <button
            type="button"
            onClick={() => setPreviewAttachment(null)}
            aria-label="Cerrar"
            className="absolute top-5 right-5 inline-flex items-center justify-center w-10 h-10 rounded-full bg-white/15 hover:bg-white/25 text-white backdrop-blur-sm transition-colors"
          >
            <X className="w-5 h-5" />
          </button>
          <div
            className="flex flex-col items-center gap-4 max-w-[88vw]"
            onClick={(e) => e.stopPropagation()}
          >
            {(() => {
              const src =
                miniaturaDeAdjunto(previewAttachment);
              return src ? (
                <img
                  src={src}
                  alt={
                    previewAttachment.kind === 'image'
                      ? 'Imagen'
                      : previewAttachment.name
                  }
                  className="max-w-full max-h-[72vh] rounded-2xl object-contain shadow-2xl"
                />
              ) : (
                <span className="inline-flex items-center justify-center w-40 h-40 rounded-2xl bg-white/10 text-white">
                  {previewAttachment.kind === 'model' ? (
                    <User className="w-16 h-16" />
                  ) : (
                    <ImageIcon className="w-16 h-16" />
                  )}
                </span>
              );
            })()}
            {previewAttachment.kind !== 'image' && (
              <span className="text-white/80 text-[15px] font-medium text-center">
                {previewAttachment.name}
              </span>
            )}
          </div>
        </div>
      )}

      {/* Candado de las dos opciones pagas del modo —
          ADR-2026-08-20-crear-desde-cero-se-abre-y-el-candado-baja-a-las-opciones.
          Se reusa `FeatureLockedModal` (el mismo que veía este modo cuando estaba
          bloqueado entero) en vez de inventar un cartel nuevo: es el lenguaje de
          upsell que la app ya tiene, y también se portalea a `document.body`. */}
      <FeatureLockedModal
        open={!!lockOpcion}
        onClose={() => setLockOpcion(null)}
        title={lockOpcion?.title ?? ''}
        message={lockOpcion?.message ?? ''}
        ctaLabel="Mejorar plan"
      />

      {/* Confirmación de borrado del historial —
          ADR-2026-09-08-la-confirmacion-de-borrar-es-un-modal-compartido.
          Se monta acá arriba y no dentro del panel del historial: el panel se
          desmonta al cerrarse, y el modal tiene que poder sobrevivirlo. */}
      <ModalConfirmar
        abierto={pedirBorrado !== null}
        /* El conteo es el de MIS chats, no el de la lista: el historial es por
           empresa y antes el título decía "¿Borrar los 20 chats?" contando los de
           los compañeros, que además se borraban de verdad
           (ADR-2026-09-11-el-borrado-del-historial-no-cruza-usuarios). */
        titulo={
          pedirBorrado?.tipo === 'uno'
            ? `¿Borrar "${pedirBorrado.chat.title || 'Chat'}"?`
            : misChats.length === 1
              ? '¿Borrar tu único chat?'
              : `¿Borrar tus ${misChats.length} chats?`
        }
        // El detalle dice lo que NO se pierde: sin eso, "se borra la conversación" se
        // lee como que también se van las imágenes que salieron de ella. Y en el
        // "todos" aclara el alcance: sólo los propios, aunque la lista muestre más.
        detalle={
          pedirBorrado?.tipo === 'uno'
            ? 'Se borra la conversación y no se puede deshacer. Las imágenes que generaste en ella siguen en tus creaciones.'
            : misChats.length < historialChats.length
              ? `Se borran tus ${misChats.length} conversaciones y no se puede deshacer. Las de tus compañeros no se tocan. Las imágenes que generaste siguen en tus creaciones.`
              : 'Se borran todas tus conversaciones y no se puede deshacer. Las imágenes que generaste en ellas siguen en tus creaciones.'
        }
        // El botón dice la acción, no "Aceptar": es lo último que se lee antes de que
        // no haya vuelta atrás.
        confirmar={
          pedirBorrado?.tipo === 'uno'
            ? 'Borrar chat'
            : misChats.length === 1
              ? 'Borrar chat'
              : `Borrar los ${misChats.length}`
        }
        trabajando={borrandoChats}
        onCancelar={() => setPedirBorrado(null)}
        onConfirmar={confirmarBorrado}
      />
    </>,
    document.body,
  );
};

/** Item del menú de adjuntos del Asistente de marketing — fila full-width
 *  con icon + label + chevron. Usado tanto en el popup desktop como en el
 *  bottom sheet mobile. */
/**
 * Fila del menú "Agregar" de v2 — ADR-2026-09-08-el-menu-agregar-de-v2.
 *
 * Otra forma que `AttachMenuItem`, y a propósito: ícono suelto (no en pastilla),
 * rótulo y descripción en la MISMA línea, sin chevron. Es el patrón de la referencia
 * que pidió el diseño, y lee como una lista de orígenes en vez de una de navegación.
 *
 * Dos flags para orígenes que todavía no funcionan, y NO son lo mismo:
 *
 *   • `proximamente` — fila visible pero apagada y muerta (gris, `disabled`). Hoy no la
 *     usa nadie; queda porque es el estado honesto para un origen que no anda.
 *
 *   • `artificial` — fila que se ve y se comporta como HABILITADA (color pleno, hover,
 *     cursor), pero cuyo click sólo cierra el menú. Es una MAQUETA, pedida a propósito
 *     para poder mostrar el menú completo en v2 (que es `devOnly`, o sea una variante en
 *     prueba) mientras el origen real no existe. Hoy la lleva **Videos**.
 *
 *     Grepear `artificial` para encontrar todo lo que hay que sacar cuando Videos ande
 *     de verdad. Y ojo: NO tiene que llegar a producción así — los adjuntos del chat son
 *     `image | product | model` y la API del agente acepta únicamente media types de
 *     imagen, así que un video no tiene por dónde viajar; habilitarlo de verdad pide un
 *     tipo de adjunto nuevo y soporte del lado del agente
 *     (ADR-2026-09-08-videos-habilitado-de-mentira).
 */
const AgregarMenuItem: React.FC<{
  icon: React.ReactNode;
  label: string;
  descripcion: string;
  onClick?: () => void;
  proximamente?: boolean;
  /** Maqueta: se ve habilitada pero no hace nada. Ver el comentario de arriba. */
  artificial?: boolean;
}> = ({
  icon,
  label,
  descripcion,
  onClick,
  proximamente = false,
  artificial = false,
}) => (
  <button
    type="button"
    onClick={proximamente ? undefined : onClick}
    disabled={proximamente}
    // `artificial` NO entra acá: la fila tiene que verse igual que una viva. El
    // atributo queda abajo, en el DOM, para que se pueda ver desde el inspector
    // que esta fila es una maqueta.
    data-artificial={artificial || undefined}
    className={`w-full flex items-center gap-3 px-3 py-2.5 rounded-xl text-left transition-colors ${
      proximamente
        ? 'opacity-45 cursor-not-allowed'
        : 'hover:bg-neutral-100 active:bg-neutral-200'
    }`}
  >
    <span className="inline-flex items-center justify-center w-5 h-5 shrink-0 text-brand-black/80">
      {icon}
    </span>
    <span className="min-w-0 flex items-baseline gap-2">
      <span className="text-[14.5px] font-medium text-brand-black/90 whitespace-nowrap">
        {label}
      </span>
      <span className="text-[13px] text-[#3C3C43]/50 truncate">
        {descripcion}
      </span>
    </span>
  </button>
);

const AttachMenuItem: React.FC<{
  icon: React.ReactNode;
  label: string;
  onClick: () => void;
}> = ({ icon, label, onClick }) => (
  <button
    type="button"
    onClick={onClick}
    className="w-full flex items-center justify-between gap-3 px-3 py-3 rounded-xl hover:bg-neutral-50 active:bg-neutral-100 text-left transition-colors"
  >
    <span className="inline-flex items-center gap-3 text-[14.5px] text-brand-black/90">
      <span className="inline-flex items-center justify-center w-8 h-8 rounded-full bg-brand-primary/10 text-brand-primary shrink-0">
        {icon}
      </span>
      {label}
    </span>
    <ChevronRight className="w-4 h-4 text-[#3C3C43]/40 shrink-0" />
  </button>
);
